import type { MarketFacts } from '@makan/contracts/client';
import { byLocale, type Words } from '@makan/command-center/kit';
import type { SignUpRow } from '../domain/index.ts';
import type { GENDERS, RACES } from '../domain/values.ts';

/** A gender as the member picks it (the domain's value set). */
type Gender = (typeof GENDERS)[number];
/** A race as the member picks it (the domain's value set). */
type Race = (typeof RACES)[number];

/** A market's phone examples: what a phone field's hint and its error show, so a market adds no string. */
type PhoneFacts = MarketFacts['phone'];
/** A count as the market writes it (the kit's MarketFormat `count`): copy with a number in it takes this. */
type Count = (value: number) => string;
/** The market's currency symbol, like RM: a label naming the currency takes it. */
type Currency = string;

/**
 * Every word crm's pages show, by locale (the kit holds the shell's and the
 * shared ones): one object per locale, each a `CrmStrings`, so the compiler
 * names every missing or extra string. Copy with a value in it is a
 * function, so word order can differ per language. The zh words are
 * Simplified Chinese as Malaysian diners and staff read it; money keeps the
 * market's symbol (RM5).
 *
 * The PDPA notice (`join.notice` in short, `privacy` in full) is a draft
 * for the group, the data controller, to approve: the page marks it so,
 * under the notice version `join` quotes. The English version prevails.
 */
export interface CrmStrings {
  readonly join: {
    readonly brandSub: string;
    /** The brand or group whose page it is; none while it is not known. */
    readonly title: (name: string | null) => string;
    readonly lead: string;
    readonly phoneLabel: string;
    /** `example`: a national number of the picked country when it is the market's, else null. */
    readonly phoneHint: (example: string | null) => string;
    readonly phoneInvalid: (example: string | null) => string;
    readonly sendCode: string;
    readonly sending: string;
    /** Too many codes for one phone: the real wait in minutes, when the answer names it. */
    readonly tooManyCodes: (minutes: number | null) => string;
    readonly codeBudgetSpent: string;
    /** A voucher's QR whose voucher its campaign no longer gives on its own. */
    readonly voucherGone: string;
    readonly codeTitle: string;
    readonly codeSentTo: (phone: string) => string;
    readonly changeNumber: string;
    /** The code field's label, naming the phone it went to. */
    readonly codeLabel: (phone: string) => string;
    readonly codeInvalid: string;
    readonly codeWrong: (triesLeft: number) => string;
    readonly resend: string;
    readonly resendIn: (seconds: number) => string;
    readonly resent: string;
    /** Under the code field when the gateway could not send the code (code-delivery `not delivered`): the resend button works at once. */
    readonly notDelivered: string;
    readonly codeExpired: string;
    readonly tooManyAttempts: string;
    readonly noCodeSent: string;
    readonly startAgain: string;
    readonly challengeGone: string;
    readonly noticeOutdated: string;
    readonly reload: string;
    readonly detailsHeading: string;
    readonly nameLabel: string;
    readonly nameRequired: string;
    readonly genderLabel: string;
    readonly genders: Readonly<Record<Gender, string>>;
    readonly genderRequired: string;
    readonly raceLabel: string;
    readonly races: Readonly<Record<Race, string>>;
    readonly raceRequired: string;
    readonly year: string;
    readonly birthdayLabel: string;
    readonly inviteLabel: string;
    readonly inviteHint: string;
    readonly inviteInvalid: string;
    readonly inviteUnknown: string;
    readonly day: string;
    readonly month: string;
    readonly months: readonly string[];
    /** The months as the birthday select shows them, short enough for three selects on a phone. */
    readonly monthsShort: readonly string[];
    readonly birthdayIncomplete: string;
    readonly birthdayNoSuchDate: string;
    /** Who keeps the data, when the group's name is not known yet. */
    readonly controllerUnknown: string;
    readonly noticeHeading: string;
    readonly noticeDraftBadge: (version: string, controller: string) => string;
    /** The notice in short, beside the questions it covers; the full notice is the privacy page. */
    readonly notice: (controller: string) => readonly string[];
    readonly noticeFull: string;
    readonly marketingHeading: string;
    readonly marketingLead: (controller: string) => string;
    /** Each channel is a question answered yes or no: neither is picked until the person picks one. */
    readonly marketingWhatsApp: string;
    readonly marketingSms: string;
    readonly marketingAnswers: Readonly<Record<'yes' | 'no', string>>;
    readonly marketingRequired: string;
    readonly agreement: string;
    readonly submit: string;
    readonly submitting: string;
    readonly erasedTitle: string;
    readonly erasedBody: string;
    /** The link's group was never opened: no form, nothing a retry could fix. */
    readonly linkInvalidTitle: string;
    readonly linkInvalidBody: string;
    /** The same, in one line: what a send answers when the page learns it only then. */
    readonly linkInvalid: string;
  };
  /**
   * The full PDPA notice (s.7 of the Act), in the order Malaysian F&B
   * notices use. The group is the data controller; its registered name and
   * its contact are bracketed placeholders until it gives them.
   */
  readonly privacy: {
    readonly title: string;
    readonly lead: (controller: string) => string;
    readonly sections: (controller: string) => readonly { readonly heading: string; readonly paragraphs: readonly string[] }[];
  };
  readonly members: {
    readonly title: string;
    /** The staff's alert when the day's sign-up codes are spent (sign-up codes paused). */
    readonly codesPaused: (budget: string) => string;
    /** The staff's count of the day's sign-up codes not delivered (sign-up code not delivered), shown while it is above 0. */
    readonly codesNotDelivered: (failed: string, tried: string) => string;
    /** The list behind that count, for a manager: each sign-up whose latest failed attempt was today, its phone masked. */
    readonly undelivered: {
      readonly open: string;
      readonly title: string;
      readonly lead: string;
      readonly columns: { readonly phone: string; readonly time: string; readonly reason: string; readonly source: string; readonly since: string };
      readonly reasons: Readonly<Record<NonNullable<SignUpRow['lastDeliveryFailure']>, string>>;
      readonly since: { readonly joined: string; readonly gotCode: string; readonly noCode: string };
      /** The phone, once the sign-up's record of it is gone (kept a day, deleted by an erasure). */
      readonly noPhone: string;
      /** Under the list when more failed today than it shows (the latest 50). */
      readonly more: (count: string) => string;
    };
    readonly refresh: string;
    readonly refreshing: string;
    readonly totalMembers: string;
    readonly totalErased: (count: string) => string;
    readonly bySource: string;
    readonly byDate: string;
    readonly funnel: string;
    readonly funnelStarted: string;
    readonly funnelSent: string;
    readonly funnelJoined: string;
    readonly funnelNotDelivered: string;
    readonly funnelWrongCodes: string;
    readonly noMembers: string;
    readonly noData: string;
    readonly columns: { readonly member: string; readonly source: string; readonly whatsApp: string; readonly sms: string; readonly visits: string; readonly lastVisit: string; readonly since: string; readonly mostVisited: string; readonly race: string; readonly gender: string; readonly age: string };
    /** A member with no visit yet, in the last visit column; `noValue` in the columns that need one. */
    readonly never: string;
    readonly noValue: string;
    /** Calendar days from the last visit's date to today, in the market's zone: "Today", "1 day", "12 days". */
    readonly sinceDays: (days: number, count: (value: number) => string) => string;
    /** An outlet the outlets list no longer names. */
    readonly unknownOutlet: string;
    /** The personal columns while the reveal is on its way, and when it failed. */
    readonly revealing: string;
    readonly revealFailed: string;
    /** The sentence under the title: the total and how many joined in the last 7 days, written by `count`. */
    readonly summary: (total: number, week: number, count: (value: number) => string) => Words;
    readonly kpiWeek: string;
    readonly kpiWeekNote: (from: string) => string;
    readonly kpiConversion: string;
    readonly kpiConversionNote: (joined: string, asked: string) => string;
    readonly kpiConsent: string;
    readonly kpiConsentNote: (shown: string) => string;
    readonly search: string;
    /** Beside a member's masked phone in the list: their member number. */
    readonly numbered: (number: string) => string;
    readonly sourceFilter: string;
    readonly allSources: string;
    /** The filters sheet: what narrows the members list besides the search. */
    readonly filters: {
      readonly label: string;
      readonly clear: string;
      readonly done: (shown: string) => string;
      readonly medium: string;
      readonly allMediums: string;
      readonly noMedium: string;
      readonly country: string;
      readonly allCountries: string;
      readonly gender: string;
      readonly allGenders: string;
      readonly age: string;
      readonly allAges: string;
      /** An age band: "18 to 24", "55 and over", "under 18". */
      readonly band: (from: number, to: number | null) => string;
      readonly birthMonth: string;
      readonly allMonths: string;
      readonly joinedFrom: string;
      readonly joinedUntil: string;
      readonly notGiven: string;
      readonly lastVisit: string;
      readonly lastOutlet: string;
      readonly spend: string;
      readonly soonLead: string;
    };
    readonly export: {
      readonly label: string;
      readonly busy: string;
      readonly failed: string;
      readonly fileName: (date: string) => string;
      readonly columns: {
        readonly memberNumber: string;
        readonly name: string;
        readonly phone: string;
        readonly gender: string;
        readonly yearOfBirth: string;
        readonly birthMonth: string;
        readonly country: string;
        readonly source: string;
        readonly medium: string;
        readonly utmSource: string;
        readonly utmCampaign: string;
        readonly joined: string;
        readonly whatsApp: string;
        readonly sms: string;
        readonly vouchers: string;
        readonly used: string;
        readonly visits: string;
        readonly lastVisit: string;
        readonly lastOutlet: string;
        readonly daysSince: string;
        readonly mostVisited: string;
        readonly race: string;
      };
    };
    readonly showing: (shown: string, loaded: string) => string;
    readonly noMatch: string;
    readonly open: (name: string) => string;
    readonly erasedOn: (date: string) => string;
    readonly sheet: {
      readonly profile: string;
      readonly memberNumber: string;
      readonly phone: string;
      readonly gender: string;
      readonly yearOfBirth: string;
      readonly race: string;
      readonly country: string;
      readonly medium: string;
      readonly campaign: string;
      readonly name: string;
      readonly source: string;
      readonly joined: string;
      readonly offers: string;
      readonly yes: string;
      readonly no: string;
      readonly vouchers: string;
      readonly vouchersCount: (given: number, used: number) => string;
      readonly vouchersLoading: string;
      readonly vouchersFailed: string;
      readonly noVouchers: string;
      readonly points: string;
      readonly pointsLead: string;
      readonly tier: string;
      readonly tierLead: string;
      readonly visits: string;
      /** The visits section's chip: "1 visit", "12 visits". */
      readonly visitsCount: (visits: number, count: (value: number) => string) => string;
      readonly lastVisit: string;
      readonly mostVisited: string;
      readonly noVisits: string;
    };
    readonly noName: string;
    readonly erased: string;
    readonly showMore: string;
    readonly loading: string;
    readonly loadFailed: string;
    readonly retry: string;
    readonly shareTitle: string;
    readonly shareLead: string;
    readonly shareTagLabel: string;
    readonly shareTagHint: string;
    readonly shareTagInvalid: string;
    readonly shareEmpty: string;
    readonly shareCopy: string;
    readonly shareCopied: string;
    readonly shareCopyFailed: string;
    readonly shareQrLabel: (link: string) => string;
    readonly shareQrHint: string;
  };
  readonly channels: {
    readonly whatsApp: string;
    readonly sms: string;
  };
  readonly member: {
    readonly title: string;
    readonly welcomeJoined: (name: string | null) => string;
    readonly welcomeBack: string;
    readonly qrTitle: string;
    readonly qrLabel: string;
    readonly qrHint: string;
    readonly vouchersTitle: string;
    readonly loading: string;
    readonly loadFailed: string;
    readonly noVouchers: string;
    readonly endedTitle: (count: number) => string;
    readonly entered: string;
    /** A card refused because the member already claimed as many cards of its campaign as one member may. */
    readonly cardLimit: string;
    readonly enterRefused: Readonly<Record<'not a member' | 'member erased' | 'offer not in campaign' | 'already entered' | 'draw used' | 'card used' | 'card void' | 'campaign not open' | 'campaign ended' | 'campaign full' | 'over budget', string>>;
    readonly drawRefused: Readonly<Record<'not a member' | 'member erased' | 'already entered' | 'draw used' | 'card used' | 'card void' | 'campaign not open' | 'campaign ended' | 'campaign full' | 'over budget', string>>;
    readonly withheld: Readonly<Record<'campaign not open' | 'campaign ended' | 'campaign full' | 'over budget' | 'already entered' | 'draw used' | 'card used' | 'card void', string>>;
    readonly inviteTitle: string;
    readonly inviteCodeLabel: string;
    readonly inviteLead: string;
    readonly inviteCount: (joined: number, rewards: number) => string;
    readonly inviteWhatsApp: string;
    readonly inviteCopy: string;
    readonly inviteCopyCode: string;
    readonly inviteCodeCopied: string;
    /** The WhatsApp message a member sends: their link, and their code for a friend who joins another way. */
    readonly inviteMessage: (link: string, code: string, name: string | null) => string;
    /** Before the sign-out link, in the same line. */
    readonly notYou: string;
    readonly signOut: string;
    readonly replaced: string;
    readonly signInTitle: string;
    readonly signInLead: string;
    readonly signIn: string;
    readonly signingIn: string;
    readonly notMemberTitle: string;
    readonly notMemberBody: string;
    readonly joinNow: string;
    /** The tier and cashback cards, as a screen reader names them. */
    readonly standingTitle: string;
    readonly tierLabel: string;
    /** The bar to the next tier, as a screen reader names it: the points earned of those the next tier starts at, and its name. */
    readonly tierNote: (earned: string, from: string, next: string) => string;
    /** Over the bar, at its start: the next tier, its name drawn bold. */
    readonly tierTo: (next: string) => Words;
    /** Over the bar, at its end: the points earned (drawn bold) of those the next tier starts at. */
    readonly tierCount: (earned: string, from: string) => Words;
    /** Over the member's name in the band, by the time of day where the group is. */
    readonly greeting: Readonly<Record<'morning' | 'afternoon' | 'evening', string>>;
    /** The invite card's line that sells it, and the line under it: `name` is the brand or group, null while it is not known. */
    readonly inviteHeadline: string;
    readonly inviteBlurb: (name: string | null) => string;
    /** Under the last tier. */
    readonly tierTop: string;
    readonly cashbackLabel: string;
    /** Under the cashback: the points it is, which are the same thing. */
    readonly cashbackPoints: (points: number, format: (value: number) => string) => string;
    /** The cashback card's info button, as a screen reader names it. */
    readonly cashbackInfoLabel: string;
    /** Its bubble: how points are earned, as the points rules set it ("RM1 spent = 1 point"). */
    readonly cashbackInfo: (spent: string, points: number, format: (value: number) => string) => string;
    /** Beside the member QR: the number staff type or read out. */
    readonly memberNumber: string;
    readonly cardRow: string;
    readonly cardRowSub: string;
    readonly cardLarge: string;
    readonly vouchersRowSub: (usable: number) => string;
    readonly storeRow: string;
    readonly storeRowSub: string;
    readonly qrShow: string;
    readonly ready: (count: number, format: (value: number) => string) => string;
    readonly fromReferral: string;
    readonly fromReferred: string;
    readonly more: string;
  };
  readonly lookup: {
    readonly title: string;
    readonly phoneLabel: string;
    readonly phoneHint: (phone: PhoneFacts) => string;
    readonly phoneInvalid: (phone: PhoneFacts) => string;
    readonly find: string;
    readonly finding: string;
    readonly notFound: string;
    readonly erased: string;
    readonly joined: (date: string, source: string) => string;
    /** A found member's number, which they read out. */
    readonly memberNumber: (number: string) => string;
    readonly noName: string;
    readonly vouchers: string;
    readonly noVouchers: string;
    readonly clear: string;
    readonly failed: string;
  };
  readonly redeem: {
    readonly title: string;
    readonly outletLabel: string;
    readonly outletPick: string;
    readonly outletMissing: string;
    readonly noOutlets: string;
    readonly setupTitle: string;
    readonly setupLead: string;
    readonly stationName: string;
    readonly stationNameHint: string;
    readonly stationNameMissing: string;
    readonly setupSubmit: string;
    readonly settingUp: string;
    readonly setupCancel: string;
    readonly setupFailed: string;
    readonly stationAt: (outlet: string, station: string) => string;
    readonly change: string;
    readonly stationGone: string;
    readonly scanTitle: string;
    readonly scanLead: string;
    readonly startCamera: string;
    readonly stopCamera: string;
    readonly cameraUnavailable: string;
    readonly passLabel: string;
    readonly passHint: string;
    readonly find: string;
    readonly finding: string;
    readonly unknownPass: string;
    readonly memberTitle: (who: string) => string;
    readonly noName: string;
    readonly noVouchers: string;
    readonly choose: string;
    readonly redeem: (foodOnly: boolean | null) => string;
    readonly redeeming: string;
    readonly redeemed: string;
    readonly redeemedAt: (outlet: string, time: string) => string;
    readonly nextCustomer: string;
    readonly askManager: string;
    readonly loadFailed: string;
    readonly refused: Readonly<Record<'scan the member QR again' | 'one voucher per visit' | "not this member's voucher" | 'already redeemed' | 'revoked' | 'not valid yet' | 'expired' | 'outside its days and hours' | 'another outlet' | 'another brand', string>>;
  };
  readonly campaigns: {
    readonly title: string;
    /** The sentence under the title: open campaigns, vouchers given and used, written by `count`. */
    readonly summary: (open: number, given: number, used: number, count: (value: number) => string) => Words;
    readonly details: string;
    readonly live: (count: number, format: (value: number) => string) => string;
    readonly newCampaign: string;
    readonly loading: string;
    readonly loadFailed: string;
    readonly retry: string;
    readonly none: string;
    readonly noOnJoin: string;
    readonly managersOnly: string;
    readonly makeOnJoin: string;
    readonly open: string;
    readonly ended: string;
    readonly starts: (date: string) => string;
    readonly kind: Readonly<Record<'on join' | 'by link' | 'by draw' | 'by card' | 'referral' | 'to audience', string>>;
    readonly gives: (vouchers: readonly string[], rule: 'all' | 'random one') => string;
    /** A card campaign's rewards: each card gives the one of its print run's range. */
    readonly givesPerCard: (vouchers: readonly string[]) => string;
    readonly drawn: (pressed: number, claimed: number, count: Count) => string;
    readonly station: string;
    /** After the count taken, in the results sentence: "12 taken, 3 used, 25% of those taken." */
    readonly taken: string;
    /** Between the taken and the used figures in a campaign's results line. */
    readonly and: string;
    readonly used: (rate: string) => string;
    /** After the money used: "RM30 of face value used." */
    readonly valueUsed: string;
    readonly endedFold: (ended: number, count: Count) => string;
    readonly given: (given: readonly { readonly count: number; readonly what: string }[], count: Count) => string;
    readonly usedSplit: (used: readonly { readonly count: number; readonly what: string }[], count: Count) => string;
    readonly turnedAway: (count: number) => string;
    readonly cardsTurnedAway: (count: number) => string;
    readonly lift: (treated: string, holdout: string) => string;
    readonly showQr: string;
    readonly cards: string;
    readonly end: string;
    readonly endConfirm: string;
    readonly ending: string;
    readonly keep: string;
    readonly endFailed: string;
  };
  readonly eventQr: {
    readonly title: string;
    readonly lead: Readonly<Record<'by link' | 'by draw' | 'by card' | 'on join' | 'referral', string>>;
    readonly name: string;
    readonly nameHint: string;
    readonly gives: string;
    readonly givesBy: Readonly<Record<'by link' | 'by draw' | 'by card' | 'on join' | 'referral', string>>;
    readonly rewardN: (n: number) => string;
    readonly addReward: string;
    readonly remove: string;
    readonly removeReward: (title: string) => string;
    readonly rule: string;
    readonly rules: Readonly<Record<'all' | 'random one', string>>;
    /** The three ways a campaign of several vouchers can give them, in the owner's words. */
    readonly ruleHint: string;
    readonly weight: string;
    /** Under a weight: the voucher's chance from the weights typed so far ("9 of 10, 90%"), or how a weight works while one is not a whole number. */
    readonly weightHint: (chance: string | null) => string;
    /** How many of a voucher the campaign issues, left empty for no limit. */
    readonly quantity: string;
    readonly quantityHint: string;
    readonly benefit: string;
    readonly benefitKind: string;
    readonly benefits: (currency: Currency) => Readonly<Record<'amount off' | 'percent off' | 'free item' | 'free grams', string>>;
    readonly amount: (currency: Currency) => string;
    readonly percent: string;
    readonly percentCap: (currency: Currency) => string;
    readonly item: string;
    readonly grams: string;
    readonly days: string;
    readonly validDays: string;
    readonly validDaysHint: string;
    readonly foodOnly: string;
    readonly more: string;
    readonly hours: string;
    readonly hoursFrom: string;
    readonly hoursUntil: string;
    readonly minSpend: (currency: Currency) => string;
    readonly minGrams: string;
    readonly startsOn: string;
    readonly startsOnHint: string;
    readonly outlets: string;
    readonly outletsHint: string;
    readonly brands: string;
    readonly brandsHint: string;
    readonly giveFrom: string;
    readonly giveUntil: string;
    readonly cap: string;
    readonly capHint: string;
    readonly budget: (currency: Currency) => string;
    readonly budgetHint: string;
    readonly source: string;
    readonly sourceHint: string;
    readonly alsoOn: string;
    readonly alsoOnHint: string;
    readonly routes: Readonly<Record<'event QR' | 'draw' | 'printed card' | 'referral', string>>;
    readonly referralWhen: string;
    readonly referralWhenBy: Readonly<Record<'on sign-up' | 'on first redemption', string>>;
    readonly referralTo: string;
    readonly referralToBy: Readonly<Record<'inviter' | 'invitee' | 'both', string>>;
    readonly perInviter: string;
    readonly perInviterHint: string;
    readonly cardCount: string;
    readonly cardLabel: string;
    readonly cardLabelHint: string;
    readonly serials: string;
    readonly serialsPreview: (total: number, first: string, last: string, count: Count) => string;
    readonly prefix: string;
    readonly prefixHint: string;
    readonly digits: string;
    readonly firstSerial: string;
    readonly perMember: string;
    readonly perMemberHint: string;
    readonly optional: string;
    readonly submit: Readonly<Record<'by link' | 'by draw' | 'by card' | 'on join' | 'referral', string>>;
    readonly submitting: Readonly<Record<'by link' | 'by draw' | 'by card' | 'on join' | 'referral', string>>;
    readonly cancel: string;
    readonly invalid: {
      readonly name: string;
      readonly amount: (currency: Currency) => string;
      readonly percent: string;
      readonly item: string;
      readonly grams: string;
      readonly days: string;
      readonly validDays: string;
      readonly hours: string;
      readonly whole: string;
      readonly until: string;
      readonly source: string;
      readonly cards: string;
      readonly perInviter: string;
      readonly tooMany: (max: number, count: Count) => string;
      readonly prefix: string;
      readonly digits: string;
      readonly moreDigits: string;
      readonly first: string;
      readonly weight: (max: number) => string;
      readonly quantity: (max: number, count: Count) => string;
    };
    readonly refused: Readonly<Record<'source in use' | 'ends before it starts' | 'referral campaign open', string>>;
    readonly failed: Readonly<Record<'by link' | 'by draw' | 'by card' | 'on join' | 'referral', string>>;
  };
  readonly voucherStation: {
    readonly title: string;
    readonly brandSub: string;
    readonly pressTitle: string;
    readonly pressLead: string;
    readonly press: string;
    readonly drawing: string;
    readonly scanTitle: string;
    readonly scanLead: string;
    readonly qrLabel: string;
    readonly left: (seconds: number) => string;
    readonly done: string;
    readonly askStaffTitle: string;
    readonly offline: string;
    readonly closed: string;
    readonly tryAgain: string;
    readonly unsetTitle: string;
    readonly unsetLead: string;
    readonly signIn: string;
    readonly signInAsManager: string;
    readonly setupTitle: string;
    readonly setupLead: string;
    readonly campaign: string;
    readonly campaignPick: string;
    readonly campaignMissing: string;
    readonly noDraws: string;
    readonly name: string;
    readonly nameHint: string;
    readonly nameMissing: string;
    readonly setupSubmit: string;
    readonly setupFailed: string;
  };
  readonly stationLink: {
    readonly lead: string;
  };
  /** The default terms a new voucher starts with, the group's and each brand's: the Vouchers page's Default terms sheet. */
  readonly defaultTerms: {
    readonly open: string;
    readonly title: string;
    readonly lead: string;
    readonly leadBrands: string;
    readonly scope: string;
    readonly everyBrand: string;
    readonly label: string;
    readonly save: string;
    readonly saving: string;
    readonly saved: string;
    readonly loadFailed: string;
    readonly failed: string;
  };
  /** One voucher's own terms and conditions, in the forms that make or edit it. */
  readonly terms: {
    readonly legend: string;
    readonly hintNew: string;
    readonly hintEdit: string;
    readonly line: (n: number) => string;
    readonly remove: (n: number) => string;
    readonly add: string;
  };
  readonly printRuns: {
    readonly lead: string;
    readonly loading: string;
    readonly loadFailed: string;
    readonly none: string;
    readonly serials: (first: string, last: string) => string;
    readonly made: (date: string) => string;
    readonly cards: string;
    readonly reward: string;
    readonly scanned: string;
    readonly claimed: string;
    readonly used: string;
    readonly voided: string;
    readonly download: string;
    /** The address the cards' QR codes open: the one the manager's browser is on. */
    readonly linksTo: (host: string) => string;
    readonly notPublic: (host: string) => string;
    readonly csvHeader: readonly string[];
    readonly sheet: string;
    readonly void: string;
    readonly voidLead: string;
    readonly firstSerial: string;
    readonly lastSerial: string;
    readonly voidSubmit: (cards: number | null, count: Count) => string;
    readonly voiding: string;
    readonly keep: string;
    readonly invalidSerial: string;
    readonly outside: string;
    readonly voidFailed: string;
    readonly qrLabel: (serial: string) => string;
    readonly back: string;
  };
  readonly campaignQr: {
    readonly lead: string;
    readonly label: (link: string) => string;
    readonly copy: string;
    readonly copied: string;
    readonly copyFailed: string;
    readonly print: string;
    readonly saveSvg: string;
    readonly savePng: string;
    readonly back: string;
  };
  /** A campaign's own page: its figures, how its vouchers split, and each voucher's. */
  readonly campaign: {
    readonly crumb: string;
    readonly summary: (kind: string, vouchers: number, dates: string, given: number, used: number, count: (value: number) => string, top: string | null) => Words;
    readonly dates: (from: string, until: string | null) => string;
    readonly exportCsv: string;
    readonly addVoucher: string;
    readonly given: string;
    readonly givenNote: (heldBack: string, opened: string) => string;
    readonly used: string;
    readonly usedNote: (rate: string) => string;
    readonly valueGiven: string;
    readonly valueUsed: string;
    readonly valueNote: string;
    readonly timeToUse: string;
    /** Under the median time to use; with none used (`median` false), says so instead. */
    readonly timeNote: (cancelled: string, expired: string, median: boolean) => string;
    readonly noneUsed: string;
    readonly minutes: (minutes: string) => string;
    readonly hours: (hours: string) => string;
    readonly within: (time: string) => string;
    readonly beyond: string;
    readonly range: (from: string, to: string) => string;
    readonly totalOf: (vouchers: number, count: (value: number) => string) => string;
    readonly days: (days: string) => string;
    readonly split: string;
    readonly splitGiven: string;
    readonly splitUsed: string;
    readonly splitEmpty: string;
    readonly vouchersTitle: string;
    readonly columns: { readonly voucher: string; readonly cards: string; readonly chance: string; readonly given: string; readonly left: string; readonly used: string; readonly rate: string; readonly valueUsed: string };
    /** In the left column, for a voucher issued without a limit. */
    readonly noLimit: string;
    /** A voucher's chance in a campaign that gives one at random: its weight of the sum, and as a share. */
    readonly chance: (weight: string, total: string, percent: string) => string;
    readonly total: string;
    readonly csvName: (name: string) => string;
    /** How the campaign's vouchers were taken, by the medium of the link: QR, link, print, station. */
    readonly byMedium: string;
    readonly byMediumEmpty: string;
    readonly qr: string;
    readonly qrTitle: (voucher: string) => string;
    /** What a voucher's own QR gives: that voucher alone, of the campaign's `vouchers`. */
    readonly qrGives: (vouchers: number) => string;
    readonly campaignQr: string;
    /** What the campaign's QR gives: all its vouchers, or one at random (then no voucher has a QR of its own). */
    /** What the campaign's QR gives: all its vouchers, or one at random by weight. */
    readonly campaignQrGives: (vouchers: number, randomOne: boolean) => string;
  };
  /** How a link was reached, as utm_medium says. */
  readonly mediums: Readonly<Record<'qr' | 'link' | 'print' | 'station', string>>;
  /** The voucher catalogue: every offer, and a new one. */
  readonly catalogue: {
    readonly title: string;
    readonly summary: (offers: number, given: number, used: number, count: (value: number) => string) => Words;
    readonly newVoucher: string;
    readonly loading: string;
    readonly loadFailed: string;
    readonly none: string;
    readonly columns: { readonly voucher: string; readonly given: string; readonly used: string; readonly status: string };
    readonly active: string;
    readonly archived: string;
    readonly revision: (revision: number) => string;
    readonly name: string;
    readonly nameHint: string;
    readonly partOf: string;
    readonly campaign: string;
    readonly campaignPick: string;
    readonly noCampaigns: string;
    readonly severalTake: string;
    readonly makeQr: string;
    readonly makeQrHint: string;
    readonly save: string;
    readonly saving: string;
    readonly saved: (name: string) => string;
    readonly savedIn: (name: string, campaign: string) => string;
    readonly qrTitle: (name: string) => string;
    readonly failed: string;
    readonly refused: (campaign: string, reason: string) => string;
    readonly invalidName: string;
    readonly invalidDays: string;
    /** Editing a voucher: publishing its next revision. */
    readonly edit: (name: string) => string;
    readonly editTitle: string;
    readonly editLead: (revision: number) => string;
    readonly editTermsKept: string;
    readonly termsCount: (lines: number) => string;
    readonly editCampaigns: (names: string) => string;
    readonly editCampaignsHint: (revision: number) => string;
    /** The edit sheet's picture: shown on every voucher of the offer, those already given too. */
    readonly picture: string;
    readonly pictureHint: string;
    readonly picturePick: string;
    readonly pictureChange: string;
    readonly pictureRemove: string;
    readonly pictureUploading: string;
    readonly pictureTooBig: string;
    readonly pictureWrongType: string;
    readonly pictureFailed: string;
    readonly editSave: string;
    readonly editSaved: (name: string, revision: number) => string;
    readonly editSavedCampaigns: (count: number) => string;
  };
  readonly voucher: {
    readonly off: string;
    readonly offUpTo: (cap: string) => string;
    readonly free: string;
    /** A weight in grams, as a voucher's value shows it: "100 g". */
    readonly grams: (grams: number) => string;
    readonly everyDay: string;
    readonly anyTime: string;
    readonly day: Readonly<Record<'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun', string>>;
    readonly from: (date: string) => string;
    readonly until: (date: string) => string;
    readonly noEnd: string;
    readonly goodFor: (days: number) => string;
    readonly foodOnly: string;
    readonly minSpend: (amount: string) => string;
    readonly minGrams: (grams: number) => string;
    readonly conditions: string;
    readonly onePerBill: string;
    readonly onlyAt: (outlets: readonly string[]) => string;
    readonly brandOnly: (brands: readonly string[]) => string;
    readonly status: Readonly<Record<'usable' | 'redeemed' | 'revoked' | 'lapsed', string>>;
  };
}

/** A controller's name at the start of a sentence: "the business" when none is set. */
const capitalised = (name: string): string => name.charAt(0).toUpperCase() + name.slice(1);

const en: CrmStrings = {
  join: {
    brandSub: 'Members',
    title: (name) => (name === null ? 'Join as a member' : `Join ${name} members`),
    lead: 'Sign up with your phone number and get your vouchers.',
    phoneLabel: 'Phone number',
    phoneHint: (example) => (example === null ? 'Pick your country, then type your mobile number.' : `Pick your country, then type your mobile number, like\u00a0${example}.`),
    phoneInvalid: (example) => (example === null ? 'Enter a mobile number of the country picked.' : `Enter a mobile number, like ${example}.`),
    sendCode: 'Send code',
    sending: 'Sending code',
    tooManyCodes: (minutes) => (minutes === null ? 'Too many codes for this number. Try again in a few minutes.' : `Too many codes for this number. Try again in ${String(minutes)} minute${minutes === 1 ? '' : 's'}.`),
    codeBudgetSpent: 'Sign-up codes are paused for today. Please ask the staff, or try again tomorrow.',
    voucherGone: 'The voucher of this QR is no longer given. Ask the staff for the current QR.',
    codeTitle: 'Check your phone',
    codeSentTo: (phone) => `We sent a 6-digit code by SMS to ${phone}.`,
    changeNumber: 'Change number',
    codeLabel: (phone) => `6-digit code we sent to ${phone}`,
    codeInvalid: 'Enter the 6-digit code from the SMS.',
    codeWrong: (triesLeft) => (triesLeft > 0 ? `That code is not right. ${String(triesLeft)} ${triesLeft === 1 ? 'try' : 'tries'} left.` : 'That code is not right.'),
    resend: 'Send a new code',
    resendIn: (seconds) => `Send a new code in ${String(seconds)} s`,
    resent: 'New code sent. The old one no longer works.',
    notDelivered: "We couldn't send the code. Please try again.",
    codeExpired: 'That code has expired. Send a new one.',
    tooManyAttempts: 'Too many wrong codes. Send a new one.',
    noCodeSent: 'The code has not arrived yet. Send a new one if it does not come.',
    startAgain: 'Start again',
    challengeGone: 'This sign-up has finished or timed out. Please start again.',
    noticeOutdated: 'This page has been updated. Reload it to see the latest privacy notice.',
    reload: 'Reload page',
    detailsHeading: 'About you',
    nameLabel: 'Name',
    nameRequired: 'Enter your name.',
    genderLabel: 'Gender',
    genders: { female: 'Female', male: 'Male' },
    genderRequired: 'Choose one.',
    raceLabel: 'Race',
    races: { malay: 'Malay', chinese: 'Chinese', indian: 'Indian', others: 'Others' },
    raceRequired: 'Choose one.',
    year: 'Year',
    birthdayLabel: 'Birthday',
    inviteLabel: 'Invite code',
    inviteHint: 'If a member gave you their code.',
    inviteInvalid: 'An invite code is 8 letters and digits, like 7KQ2M9XA.',
    inviteUnknown: 'No member has this code. Check it, or leave it empty.',
    day: 'Day',
    month: 'Month',
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    birthdayIncomplete: 'Choose the day, month and year you were born.',
    birthdayNoSuchDate: 'That month does not have that day.',
    controllerUnknown: 'the business',
    noticeHeading: 'Privacy notice',
    noticeDraftBadge: (version, controller) => `Draft from ${controller}, not final. Version ${version}`,
    notice: (controller) => [
      `${capitalised(controller)} keeps your mobile number, name, gender, birthday with the year, and race, with your visits and vouchers, to run your membership, plan its offers and send you the offers you agree to.`,
      'It is kept by the companies that run the system for us, on servers in Singapore, and never sold.',
      `You can ask ${controller} to see, correct or delete your data at any time.`,
    ],
    noticeFull: 'Read the full privacy notice',
    marketingHeading: 'Offers and news',
    marketingLead: (controller) => `Choose how ${controller} may send you offers. You can change this later.`,
    marketingWhatsApp: 'Offers on WhatsApp',
    marketingSms: 'Offers by SMS',
    marketingAnswers: { yes: 'Yes', no: 'No' },
    marketingRequired: 'Choose yes or no.',
    agreement: 'By joining you confirm you have read the privacy notice.',
    submit: 'Join',
    submitting: 'Joining',
    erasedTitle: 'This number cannot join again',
    erasedBody: 'This number was a member before, and its details have since been erased. The welcome voucher is one per phone number, so this number cannot get it again.',
    linkInvalidTitle: 'This link is not valid',
    linkInvalidBody: 'Ask the outlet for a new one.',
    linkInvalid: 'This link is not valid. Ask the outlet for a new one.',
  },
  privacy: {
    title: 'Privacy notice',
    lead: (controller) => `${capitalised(controller)} ([registered company name and number]) gives you this notice under Malaysia's Personal Data Protection Act 2010. It says what we collect when you join as a member, why, and what you can do about it.`,
    sections: (controller) => [
      {
        heading: 'What we collect',
        paragraphs: [
          'When you join: your mobile number, name, gender, birthday with the year, and race. We check your number with a one-time code sent to it by SMS.',
          'We also record the language of the page you joined on, the link or QR code you joined through, any invite code you typed, and your answer on offers by WhatsApp and by SMS.',
          'As you use your membership: the vouchers you get and use, each visit when staff find you at the counter, with the outlet and the time, and the friends who join with your invite link.',
          'We do not ask for your email, IC number, address or religion, and we collect no sensitive personal data, such as your health or your political or religious beliefs.',
        ],
      },
      {
        heading: 'Why we use it',
        paragraphs: [
          'To run your membership: to sign you in, give you your vouchers, check them at the counter, and make sure each mobile number gets the welcome voucher once.',
          'To understand our members as a whole, by age, gender and race, so we can plan our menu, our outlets and our offers.',
          'To choose offers that suit you, such as a birthday treat or an offer for regular visitors, from your visits and vouchers.',
          'To send you offers on WhatsApp or by SMS, only on the channels you said yes to, in the language you joined in.',
          'To keep the records the law asks of us, and to find and stop the misuse of vouchers.',
        ],
      },
      {
        heading: 'Who we share it with',
        paragraphs: [
          'We do not sell your data. We share it only with the companies that run the service for us, under contract, and each gets only what it needs: the provider of our membership system; Google Cloud, which hosts it in Singapore, so your data is stored outside Malaysia; the SMS provider that sends your one-time codes and SMS offers; and WhatsApp (Meta), only if you said yes to offers on WhatsApp.',
          'We give your data to the authorities only when the law requires it.',
        ],
      },
      {
        heading: 'How long we keep it',
        paragraphs: [
          'We keep your details while you are a member. When you ask us to delete them, we delete your mobile number, name, gender, birthday and race. We keep only a one-way code made from your number, which cannot be turned back into it, so the same number cannot get the welcome voucher again.',
          'Your visit and voucher history stays, linked to no name and no number, so we can count how our offers did. The log of each request made to our system holds no personal details and is deleted after 3 years.',
        ],
      },
      {
        heading: 'Your choices and rights',
        paragraphs: [
          'Every question on the form must be answered to join, except the invite code. If you leave one empty, you cannot join.',
          'Offers on WhatsApp and by SMS are your choice, each on its own, and saying no does not stop you joining. To stop either at any time, tell the staff at any outlet or write to us.',
          `You can ask ${controller} to see your data, to correct it, to give you a copy of it, or to delete it, and you can withdraw your consent at any time. Write to us at the address below; we answer within 21 days. Withdrawing your consent to keep your data ends your membership.`,
          'You can also complain to the Personal Data Protection Department (JPDP).',
        ],
      },
      {
        heading: 'How to reach us',
        paragraphs: [
          `${capitalised(controller)}, [registered company name and number], [postal address].`,
          'Data protection officer: [name], [email], [phone number].',
          'We may update this notice; the version you agreed to is recorded when you join. This notice is in English, Malay and Chinese; if they differ, the English version prevails. Last updated 29 October 2026.',
        ],
      },
    ],
  },
  members: {
    title: 'Members',
    codesPaused: (budget) => `Today's limit of ${budget} login codes is used up. New sign-up codes are paused until tomorrow, or until you raise the limit.`,
    codesNotDelivered: (failed, tried) => `${failed} of today's ${tried} login codes could not be sent. Customers can ask for a new code straight away. If this number keeps rising, the SMS service may be down.`,
    undelivered: {
      open: 'See which sign-ups',
      title: 'Codes not delivered today',
      lead: 'Each sign-up whose code could not be sent today, with its latest failed attempt. Phone numbers are partly hidden.',
      columns: { phone: 'Phone', time: 'Time', reason: 'Why', source: 'Source', since: 'Since then' },
      reasons: {
        'gateway down': 'SMS service down',
        'number rejected': 'Number refused by the SMS service',
        'channel not configured': 'SMS sending not set up',
        'timed out': 'SMS service did not answer',
        'no phone on record': 'No phone number on record',
      },
      since: { joined: 'Joined', gotCode: 'Got a code later', noCode: 'No code since' },
      noPhone: 'No longer kept',
      more: (count) => `and ${count} more`,
    },
    refresh: 'Refresh',
    refreshing: 'Refreshing',
    totalMembers: 'Total members',
    totalErased: (count) => `(${count} erased)`,
    bySource: 'By source',
    byDate: 'By date joined',
    funnel: 'Sign-ups',
    funnelStarted: 'Asked for a code',
    funnelSent: 'Code sent',
    funnelJoined: 'Joined',
    funnelNotDelivered: 'Code not delivered',
    funnelWrongCodes: 'Wrong codes typed',
    noMembers: 'No members yet. They appear here as soon as someone joins.',
    noData: 'Nothing yet',
    columns: { member: 'Member', source: 'Source', whatsApp: 'WhatsApp', sms: 'SMS', visits: 'Visits', lastVisit: 'Last visit', since: 'Since last visit', mostVisited: 'Most visited', race: 'Race', gender: 'Gender', age: 'Age' },
    never: 'Never',
    noValue: '-',
    sinceDays: (days, count) => (days <= 0 ? 'Today' : days === 1 ? '1 day' : `${count(days)} days`),
    unknownOutlet: 'Unknown outlet',
    revealing: 'Loading',
    revealFailed: 'Could not load',
    summary: (total, week, count) => [{ figure: count(total) }, total === 1 ? ' member, ' : ' members, ', { figure: count(week) }, ' joined in the last 7 days.'],
    kpiWeek: 'Joined, last 7 days',
    kpiWeekNote: (from) => `Since ${from}`,
    kpiConversion: 'Sign-up conversion',
    kpiConversionNote: (joined, asked) => `${joined} joined of ${asked} who asked for a code`,
    kpiConsent: 'Agree to offers',
    kpiConsentNote: (shown) => `WhatsApp or SMS, of ${shown} shown`,
    search: 'Search name, phone, member number or source',
    numbered: (number) => `No. ${number}`,
    sourceFilter: 'Source',
    allSources: 'All sources',
    filters: {
      label: 'Filters',
      clear: 'Clear filters',
      done: (shown) => `Show ${shown}`,
      medium: 'Came by',
      allMediums: 'Any way',
      noMedium: 'No tag',
      country: 'Country',
      allCountries: 'All countries',
      gender: 'Gender',
      allGenders: 'All genders',
      age: 'Age',
      allAges: 'All ages',
      band: (from, to) => (from === 0 && to !== null ? `Under ${String(to + 1)}` : to === null ? `${String(from)} and over` : `${String(from)} to ${String(to)}`),
      birthMonth: 'Birthday month',
      allMonths: 'Any month',
      joinedFrom: 'Joined from',
      joinedUntil: 'Joined until',
      notGiven: 'Not given',
      lastVisit: 'Last visit',
      lastOutlet: 'Last outlet',
      spend: 'Accumulated spend',
      soonLead: 'Needs kiosk bills',
    },
    export: {
      label: 'Export to Excel',
      busy: 'Exporting',
      failed: 'Could not export the members. Try again.',
      fileName: (date) => `Members ${date}`,
      columns: { memberNumber: 'Member no.', name: 'Name', phone: 'Phone', gender: 'Gender', yearOfBirth: 'Year of birth', birthMonth: 'Birthday month', country: 'Country', source: 'Source', medium: 'Came by', utmSource: 'UTM source', utmCampaign: 'UTM campaign', joined: 'Joined', whatsApp: 'Offers by WhatsApp', sms: 'Offers by SMS', vouchers: 'Vouchers', used: 'Used', visits: 'Visits', lastVisit: 'Last visit', lastOutlet: 'Last outlet', daysSince: 'Days since last visit', mostVisited: 'Most visited outlet', race: 'Race' },
    },
    showing: (shown, loaded) => `${shown} of ${loaded} shown`,
    noMatch: 'No member shown matches. Change the search, or show more members.',
    open: (name) => `Open ${name}`,
    erasedOn: (date) => `Erased on ${date}`,
    sheet: {
      profile: 'Profile',
      memberNumber: 'Member no.',
      phone: 'Phone',
      gender: 'Gender',
      yearOfBirth: 'Year of birth',
      race: 'Race',
      country: 'Country',
      medium: 'Came by',
      campaign: 'UTM campaign',
      name: 'Name',
      source: 'Source',
      joined: 'Joined',
      offers: 'Offers by',
      yes: 'Yes',
      no: 'No',
      vouchers: 'Vouchers',
      vouchersCount: (given, used) => `${String(given)} held · ${String(used)} used`,
      vouchersLoading: 'Loading the vouchers',
      vouchersFailed: 'Could not load the vouchers.',
      noVouchers: 'No vouchers yet.',
      points: 'Points',
      pointsLead: 'Points earned on each visit, and the balance to spend.',
      tier: 'Tier',
      tierLead: 'The member\'s level and what it unlocks.',
      visits: 'Visits',
      visitsCount: (visits, count) => (visits === 1 ? '1 visit' : `${count(visits)} visits`),
      lastVisit: 'Last visit',
      mostVisited: 'Most visited',
      noVisits: 'No visits yet. A visit is the member QR scanned at a cashier station.',
    },
    noName: 'Not given',
    erased: 'Erased',
    showMore: 'Show more',
    loading: 'Loading',
    loadFailed: 'Could not load the members.',
    retry: 'Try again',
    shareTitle: 'Sign-up link',
    shareLead: 'Make the link for a poster, a QR code or a campaign. Everyone who joins through it is listed with its source tag.',
    shareTagLabel: 'Source tag',
    shareTagHint: 'Where the link goes, like hoarding or poster-a.',
    shareTagInvalid: 'Use lowercase letters, digits and dashes, up to 40, like poster-a.',
    shareEmpty: 'Type a source tag to get its link.',
    shareCopy: 'Copy link',
    shareCopied: 'Copied',
    shareCopyFailed: 'Could not copy. The link is selected: copy it yourself.',
    shareQrLabel: (link) => `QR code for ${link}`,
    shareQrHint: 'Scan with a phone camera to open the link.',
  },
  channels: {
    whatsApp: 'WhatsApp',
    sms: 'SMS',
  },
  member: {
    title: 'Your membership',
    welcomeJoined: (name) => (name === null ? "Welcome, you're a member" : `Welcome to ${name} members`),
    welcomeBack: 'Welcome back',
    qrTitle: 'Your member QR',
    qrLabel: 'Your member QR code',
    qrHint: 'Scan at the counter for vouchers',
    vouchersTitle: 'My vouchers',
    loading: 'Loading your vouchers',
    loadFailed: 'Could not load your vouchers.',
    noVouchers: 'No vouchers right now.',
    endedTitle: (count) => `Used and ended (${String(count)})`,
    entered: 'The voucher from the QR you scanned is now yours.',
    cardLimit: 'You already claimed as many cards of this campaign as one member can. This card stays unused.',
    enterRefused: {
      'not a member': 'This phone is not signed in to a membership.',
      'member erased': 'This membership was deleted.',
      'offer not in campaign': 'The voucher of this QR is no longer given.',
      'already entered': 'You already have the voucher from this QR.',
      'campaign not open': 'The voucher from this QR is not available right now.',
      'campaign ended': 'The voucher from this QR has ended.',
      'campaign full': 'The vouchers from this QR have all been given out.',
      'over budget': 'The vouchers from this QR have all been given out.',
      'draw used': 'This QR was already used.',
      'card used': 'This card was already used.',
      'card void': 'This card is no longer valid.',
    },
    drawRefused: {
      'not a member': 'This phone is not signed in to a membership.',
      'member erased': 'This membership was deleted.',
      'already entered': 'You already got your reward from this draw. One reward per person.',
      'draw used': 'This QR was already used. Press the button again for a new one.',
      'card used': 'This card was already used.',
      'card void': 'This card is no longer valid.',
      'campaign not open': 'The draw is not on right now.',
      'campaign ended': 'This draw has ended.',
      'campaign full': 'All the rewards of this draw have been given out.',
      'over budget': 'All the rewards of this draw have been given out.',
    },
    withheld: {
      'campaign not open': 'The voucher from the QR you scanned is not available right now.',
      'campaign ended': 'The voucher from the QR you scanned has ended.',
      'campaign full': 'The vouchers from the QR you scanned have all been given out.',
      'over budget': 'The vouchers from the QR you scanned have all been given out.',
      'already entered': 'You already got your reward from this draw. One reward per person.',
      'draw used': 'This QR was already used, so it gave no reward. Press the button again for a new one.',
      'card used': 'This card was already used, so it gave no reward.',
      'card void': 'This card is no longer valid, so it gave no reward.',
    },
    inviteTitle: 'Invite a friend',
    inviteCodeLabel: 'Your invite code',
    inviteLead: 'Share your link, or tell friends your code to type as they join. Rewards for your invites show up in your vouchers.',
    inviteCount: (joined, rewards) => `${String(joined)} ${joined === 1 ? 'friend' : 'friends'} joined · ${String(rewards)} ${rewards === 1 ? 'reward' : 'rewards'}`,
    inviteWhatsApp: 'Share on WhatsApp',
    inviteCopy: 'Copy my link',
    inviteCopyCode: 'Copy my code',
    inviteCodeCopied: 'Code copied',
    inviteMessage: (link, code, name) => `${name === null ? 'Join as a member' : `Join ${name} members`} with my link and get your welcome voucher: ${link}\n\nOr type my invite code ${code} as you join.`,
    notYou: 'Not you?',
    signOut: 'Sign out of this phone',
    replaced: 'Your member QR moved to another phone. Sign in again to use it here.',
    signInTitle: 'Your membership',
    signInLead: 'Sign in with your phone number to see your member QR and your vouchers.',
    signIn: 'Sign in',
    signingIn: 'Signing in',
    notMemberTitle: 'This number is not a member yet',
    notMemberBody: 'Join with it to get your vouchers.',
    joinNow: 'Join now',
    standingTitle: 'My tier and cashback',
    tierLabel: 'Tier',
    tierNote: (earned, from, next) => `${earned} of ${from} to ${next}`,
    tierTo: (next) => ['To ', { figure: next }],
    tierCount: (earned, from) => [{ figure: earned }, ` / ${from} points`],
    greeting: { morning: 'Good morning,', afternoon: 'Good afternoon,', evening: 'Good evening,' },
    inviteHeadline: 'Good food is better with friends',
    inviteBlurb: (name) => `Invite friends to join ${name ?? 'us'}. You both get a reward.`,
    tierTop: 'Top tier',
    cashbackLabel: 'Cashback',
    cashbackPoints: (points, format) => (points === 1 ? '1 point' : `${format(points)} points`),
    cashbackInfoLabel: 'How cashback works',
    cashbackInfo: (spent, points, format) => `${spent} spent = ${points === 1 ? '1 point' : `${format(points)} points`}`,
    memberNumber: 'Member no.',
    cardRow: 'My membership card',
    cardRowSub: 'Your member QR, larger for the counter',
    cardLarge: 'Your member QR code, large',
    vouchersRowSub: (usable) => (usable === 1 ? '1 to use' : `${String(usable)} to use`),
    storeRow: 'Rewards store',
    storeRowSub: 'Spend points on rewards',
    qrShow: 'Show my member QR',
    ready: (count, format) => `${format(count)} ready`,
    fromReferral: 'Thanks for inviting a friend',
    fromReferred: 'For joining with a friend\'s invite',
    more: 'More for members',
  },
  lookup: {
    title: 'Find a member',
    phoneLabel: 'Phone or member number',
    phoneHint: ({ example, abroadExample }) => `A phone like ${example} or ${abroadExample}, or a member number like 100023`,
    phoneInvalid: ({ example, abroadExample }) => `Enter a phone number, like ${example} or ${abroadExample}, or a member number, like 100023.`,
    find: 'Find',
    finding: 'Finding',
    notFound: 'No member has this number.',
    erased: 'This member was erased.',
    joined: (date, source) => `Joined ${date} through ${source}`,
    memberNumber: (number) => `Member no. ${number}`,
    noName: 'No name given',
    vouchers: 'Vouchers',
    noVouchers: 'No vouchers yet.',
    clear: 'Clear',
    failed: 'Could not find the member. Try again.',
  },
  redeem: {
    title: 'Redeem',
    outletLabel: 'Outlet',
    outletPick: 'Pick the outlet',
    outletMissing: 'Pick the outlet this device is at.',
    noOutlets: 'This group has no outlets yet. A manager adds them first.',
    setupTitle: 'Set up this cashier station',
    setupLead: 'Choose the outlet this device is at, once. Staff then only scan and redeem here, and every redemption records this outlet.',
    stationName: 'Station name',
    stationNameHint: 'Like Front cashier.',
    stationNameMissing: 'Name this station, like Front cashier.',
    setupSubmit: 'Use this device here',
    settingUp: 'Setting up',
    setupCancel: 'Cancel',
    setupFailed: 'Could not set up this device. Try again.',
    stationAt: (outlet, station) => `${outlet} · ${station}`,
    change: 'Change',
    stationGone: 'This device is no longer a cashier station. Set it up again.',
    scanTitle: 'Scan the member QR',
    scanLead: 'The member opens their member page and shows the QR.',
    startCamera: 'Scan with camera',
    stopCamera: 'Stop camera',
    cameraUnavailable: 'The camera is not available. Use the scanner, or allow the camera for this page.',
    passLabel: 'Member QR',
    passHint: 'A QR scanner types it here.',
    find: 'Find member',
    finding: 'Finding member',
    unknownPass: 'This is not a member QR, or it moved to another phone. Ask the member to open their member page again.',
    memberTitle: (who) => `Member: ${who}`,
    noName: 'no name given',
    noVouchers: 'This member has no vouchers to use.',
    choose: 'Voucher to use',
    redeem: (foodOnly) => (foodOnly === null ? 'Redeem' : foodOnly ? 'Redeem · food only, one per bill' : 'Redeem · one per bill'),
    redeeming: 'Redeeming',
    redeemed: 'Redeemed',
    redeemedAt: (outlet, time) => `At ${outlet}, ${time}`,
    nextCustomer: 'Next customer',
    askManager: 'This device is not a cashier station yet. A manager signs in here once to set it up for its outlet.',
    loadFailed: 'Could not load the outlets.',
    refused: {
      'scan the member QR again': 'Scan the member QR again: the scan is more than 15 minutes old, or was made at another station.',
      'one voucher per visit': 'This visit already used a voucher. One voucher per visit.',
      "not this member's voucher": 'This voucher belongs to another member.',
      'already redeemed': 'This voucher was already used.',
      revoked: 'This voucher was withdrawn.',
      'not valid yet': 'This voucher cannot be used yet.',
      expired: 'This voucher has ended.',
      'outside its days and hours': 'This voucher cannot be used now. See its days and hours.',
      'another outlet': 'This voucher is for another outlet. See where it can be used.',
      'another brand': 'This voucher is for another brand\'s outlets.',
    },
  },
  campaigns: {
    title: 'Campaigns',
    summary: (open, given, used, count) => [{ figure: count(open) }, open === 1 ? ' campaign open, ' : ' campaigns open, ', { figure: count(given) }, ' vouchers given, ', { figure: count(used) }, ' used.'],
    details: 'Details',
    live: (count, format) => `${format(count)} live`,
    newCampaign: 'New campaign',
    loading: 'Loading',
    loadFailed: 'Could not load the campaigns.',
    retry: 'Try again',
    none: 'No campaigns yet.',
    noOnJoin: 'New members get no voucher: no open campaign gives one on joining.',
    managersOnly: 'Campaigns and their results are for managers.',
    makeOnJoin: 'Make a joining voucher',
    open: 'Open',
    ended: 'Ended',
    starts: (date) => `Starts ${date}`,
    kind: { 'on join': 'On joining', 'by link': 'Event QR', 'by draw': 'Voucher station draw', 'by card': 'Printed cards', referral: 'Invite a friend', 'to audience': 'To an audience' },
    gives: (vouchers, rule) => (vouchers.length === 1 ? (vouchers[0] ?? '') : rule === 'random one' ? `one at random of ${vouchers.join(', ')}` : vouchers.join(' + ')),
    givesPerCard: (vouchers) => (vouchers.length === 1 ? (vouchers[0] ?? '') : `one per card: ${vouchers.join(', ')}`),
    drawn: (pressed, claimed, count) => `${count(pressed)} ${pressed === 1 ? 'press' : 'presses'} at the station, ${count(claimed)} claimed.`,
    station: 'Voucher station',
    taken: 'taken',
    and: ', ',
    used: (rate) => `used, ${rate} of those taken.`,
    valueUsed: 'of face value used.',
    endedFold: (ended, count) => (ended === 1 ? '1 ended campaign' : `${count(ended)} ended campaigns`),
    given: (given, count) => `Given: ${given.map((one) => `${count(one.count)} × ${one.what}`).join(', ')}`,
    usedSplit: (used, count) => `Used: ${used.map((one) => `${count(one.count)} × ${one.what}`).join(', ')}`,
    turnedAway: (count) => `${String(count)} turned away: full, over budget or closed`,
    cardsTurnedAway: (count) => `${String(count)} turned away with a used or void card`,
    lift: (treated, holdout) => `Came back: ${treated} with the voucher, ${holdout} held out`,
    showQr: 'QR and link',
    cards: 'Cards and results',
    end: 'End',
    endConfirm: 'End now',
    ending: 'Ending',
    keep: 'Keep open',
    endFailed: 'Could not end the campaign. Try again.',
  },
  eventQr: {
    title: 'New campaign',
    lead: {
      'by link': 'Everyone who scans its QR joins or signs in, and gets its voucher once.',
      'by draw': 'A voucher station at the outlet shows a new QR at each press: whoever scans it joins or signs in and gets what the draw gave, one reward per person.',
      'by card': 'Print cards to hand out by hand, at random if you like. Each card has its own QR code that works once: whoever scans it joins or signs in and gets the voucher of its range.',
      'on join': 'Everyone who joins gets its voucher, unless they join through another campaign\'s QR.',
      referral: 'Members share their link or code. A friend who joins with it is linked to them, and the invite pays this voucher when and to whom you choose.',
    },
    name: 'Name',
    nameHint: 'Members see it on the voucher, like Opening week at TRX.',
    gives: 'How members get it',
    givesBy: { 'by link': 'Scan an event QR', 'by draw': 'Press at a voucher station', 'by card': 'Printed cards', 'on join': 'Everyone who joins', referral: 'Invite a friend' },
    rewardN: (n) => `Voucher ${String(n)}`,
    addReward: 'Add another voucher',
    remove: 'Remove',
    removeReward: (title) => `Remove ${title}`,
    rule: 'Each member gets',
    rules: { all: 'Every voucher', 'random one': 'One at random, by weight' },
    ruleHint: 'Every voucher: each person gets all of them. One at random: each scan gives one, and a voucher of weight 3 comes up three times as often as one of weight 1. To hand cards out at random by hand, choose Printed cards above.',
    weight: 'Weight',
    weightHint: (chance) => (chance === null ? 'Its chance is its weight out of all the weights.' : `Its chance: ${chance}.`),
    quantity: 'How many to issue',
    quantityHint: 'Once this many are given, the campaign stops giving this voucher. Leave it empty for no limit.',
    benefit: 'Voucher',
    benefitKind: 'What it gives',
    benefits: (currency) => ({ 'amount off': `${currency} off`, 'percent off': '% off', 'free item': 'Free item', 'free grams': 'Free grams' }),
    amount: (currency) => `Amount off (${currency})`,
    percent: 'Percent off',
    percentCap: (currency) => `Up to (${currency})`,
    item: 'Item given free',
    grams: 'Grams given free',
    days: 'Days it can be used',
    validDays: 'Good for (days)',
    validDaysHint: 'Counted from the day the member takes it, or from its first day if that is later.',
    foodOnly: 'Food only, not drinks',
    more: 'More terms',
    hours: 'Hours it can be used',
    hoursFrom: 'Usable from',
    hoursUntil: 'Usable until',
    minSpend: (currency) => `Minimum spend (${currency})`,
    minGrams: 'Minimum order (grams)',
    startsOn: 'First day it can be used',
    startsOnHint: 'Members can hold it before then, like from the hoarding to the opening.',
    outlets: 'Outlets it can be used at',
    outletsHint: 'Leave all unticked for every outlet.',
    brands: 'Brands it is for',
    brandsHint: 'Used at that brand\'s outlets, with its terms. Leave all unticked for every brand.',
    giveFrom: 'Give from',
    giveUntil: 'Give until',
    cap: 'Most vouchers to give',
    capHint: 'Scans after that are told it is full.',
    budget: (currency) => `Budget (${currency})`,
    budgetHint: 'The most face value to give, counted as vouchers are taken.',
    source: 'Link name',
    sourceHint: 'Members who join through the QR are listed with it.',
    alsoOn: 'Also for people who join through',
    alsoOnHint: 'Everyone who signs up on the poster or the page gets it. Untick a way you want to give only its own voucher.',
    routes: { 'event QR': 'An event QR', draw: 'A voucher station draw', 'printed card': 'A printed card', referral: 'A friend\'s invite' },
    referralWhen: 'Pays when',
    referralWhenBy: { 'on sign-up': 'The friend joins', 'on first redemption': 'The friend uses a first voucher' },
    referralTo: 'Who gets it',
    referralToBy: { both: 'Both', inviter: 'The member who invited', invitee: 'The friend' },
    perInviter: 'Most rewards per member',
    perInviterHint: 'Invites past this still link friends, but give the member nothing more.',
    cardCount: 'Cards',
    cardLabel: 'Label',
    cardLabelHint: 'Like the machine or flavour.',
    serials: 'Card numbers',
    serialsPreview: (total, first, last, count) => `${count(total)} cards, ${first} to ${last}`,
    prefix: 'Starts with',
    prefixHint: 'Like KM-TRX-',
    digits: 'Digits',
    firstSerial: 'First number',
    perMember: 'Cards one member can claim',
    perMemberHint: 'Leave empty for no limit.',
    optional: 'optional',
    submit: { 'by link': 'Make the QR', 'by draw': 'Make the draw', 'by card': 'Make the cards', 'on join': 'Start giving', referral: 'Start the invites' },
    submitting: { 'by link': 'Making the QR', 'by draw': 'Making the draw', 'by card': 'Making the cards', 'on join': 'Starting', referral: 'Starting the invites' },
    cancel: 'Cancel',
    invalid: {
      name: 'Give the campaign a name.',
      amount: (currency) => `Enter the amount off in ${currency}, like 5 or 2.50.`,
      percent: 'Enter a percent from 1 to 100.',
      item: 'Name the free item.',
      grams: 'Enter the free grams, like 100.',
      days: 'Pick at least one day.',
      validDays: 'Enter how many days, like 7.',
      hours: 'Give both times, the second after the first.',
      whole: 'Enter a number above zero.',
      until: 'The last day to give is before the first.',
      source: 'Use lowercase letters, digits and dashes, up to 40.',
      cards: 'Enter how many cards, like 250.',
      perInviter: 'Enter a number from 1 to 100.',
      tooMany: (max, count) => `One print run makes at most ${count(max)} cards.`,
      prefix: 'Use capitals, digits and dashes, like KM-TRX-.',
      digits: 'Enter 1 to 8 digits.',
      moreDigits: 'The last card number needs more digits.',
      first: 'Enter the first card number, like 1.',
      weight: (max) => `Enter a whole number from 1 to ${String(max)}.`,
      quantity: (max, count) => `Enter a whole number from 1 to ${count(max)}, or leave it empty.`,
    },
    refused: {
      'source in use': 'Another campaign already uses this link name. Change it under More terms.',
      'ends before it starts': 'The last day to give is before the first.',
      'referral campaign open': 'Another invite campaign is open. End it first: one runs at a time.',
    },
    failed: { 'by link': 'Could not make the QR. Try again.', 'by draw': 'Could not make the draw. Try again.', 'by card': 'Could not make the cards. Try again.', 'on join': 'Could not start giving. Try again.', referral: 'Could not start the invites. Try again.' },
  },
  voucherStation: {
    title: 'Get your voucher',
    brandSub: 'Vouchers',
    pressTitle: 'Get your voucher',
    pressLead: 'Press the button, then scan the QR with your phone camera.',
    press: 'Press for your voucher',
    drawing: 'Drawing',
    scanTitle: 'Scan with your phone',
    scanLead: 'Open your camera and point it at the code. It works once, for one person.',
    qrLabel: 'Your voucher QR code',
    left: (seconds) => `This code goes away in ${String(seconds)} s`,
    done: 'Done',
    askStaffTitle: 'Please ask staff',
    offline: 'This machine cannot reach the system right now. A staff member can help you.',
    closed: 'There are no vouchers from this machine right now. A staff member can help you.',
    tryAgain: 'Try again',
    unsetTitle: 'This device is not a voucher station yet',
    unsetLead: 'A manager signs in here to set it up for a draw.',
    signIn: 'Sign in to set up',
    signInAsManager: 'Sign in as a manager',
    setupTitle: 'Set up this voucher station',
    setupLead: 'Choose where this device stands and the draw it gives. You are signed out after, and the device runs on its own.',
    campaign: 'Draw',
    campaignPick: 'Pick the draw',
    campaignMissing: 'Pick the draw this station gives.',
    noDraws: 'No draw is open. Make one on the campaigns page first.',
    name: 'Station name',
    nameHint: 'Like Entrance.',
    nameMissing: 'Name this station, like Entrance.',
    setupSubmit: 'Make this a voucher station',
    setupFailed: 'Could not set up this device. Try again.',
  },
  stationLink: {
    lead: 'Open this link on the device at the outlet, signed in as a manager: it becomes a voucher station of this draw, with one button for customers.',
  },
  defaultTerms: {
    open: 'Default terms',
    title: 'Default terms for new vouchers',
    lead: 'A new voucher starts with these terms, and you can change them for that voucher. Saving here only fills in new vouchers: it never changes a voucher already made.',
    leadBrands: "A new voucher starts with these terms: a brand's own for a voucher of that brand, else the ones for every brand. You can change them for each voucher. Saving here only fills in new vouchers: it never changes a voucher already made.",
    scope: 'Defaults for',
    everyBrand: 'Every brand',
    label: 'Default terms',
    save: 'Save the defaults',
    saving: 'Saving',
    saved: 'Saved. New vouchers start with these terms.',
    loadFailed: 'Could not load the default terms.',
    failed: 'Could not save the defaults. Try again.',
  },
  terms: {
    legend: 'Terms and conditions',
    hintNew: 'Members read these with this voucher. They start as your default terms; change them for this voucher as you like.',
    hintEdit: 'Members read these with this voucher. Vouchers already given keep the terms they had.',
    line: (n) => `Term ${String(n)}`,
    remove: (n) => `Remove term ${String(n)}`,
    add: 'Add a term',
  },
  printRuns: {
    lead: 'Each card has its own QR code that works once. Send the printer the file, or print the sheet here.',
    loading: 'Loading',
    loadFailed: 'Could not load the cards.',
    none: 'No cards made yet.',
    serials: (first, last) => (first === last ? first : `${first} to ${last}`),
    made: (date) => `Made ${date}`,
    cards: 'Cards',
    reward: 'Voucher',
    scanned: 'Scanned',
    claimed: 'Claimed',
    used: 'Used',
    voided: 'Void',
    download: 'Download for the printer',
    linksTo: (host) => `The cards' QR codes open ${host}.`,
    notPublic: (host) => `The cards' QR codes would open ${host}, which customers' phones cannot reach. Download for the printer from the public address.`,
    csvHeader: ['serial', 'code', 'link', 'voucher', 'label'],
    sheet: 'Print QR sheet',
    void: 'Void lost cards',
    voidLead: 'A voided card is refused when scanned. Cards already claimed keep their voucher.',
    firstSerial: 'From card',
    lastSerial: 'To card',
    voidSubmit: (cards, count) => (cards === null ? 'Void' : cards === 1 ? 'Void 1 card' : `Void ${count(cards)} cards`),
    voiding: 'Voiding',
    keep: 'Cancel',
    invalidSerial: 'Enter the card numbers, like 0021, the second after the first.',
    outside: 'Those cards are not all in this print run.',
    voidFailed: 'Could not void the cards. Try again.',
    qrLabel: (serial) => `QR code of card ${serial}`,
    back: 'Back to campaigns',
  },
  campaignQr: {
    lead: 'Print it or show it on a screen. Scanning it opens the sign-up page with this voucher.',
    label: (link) => `QR code for ${link}`,
    copy: 'Copy link',
    copied: 'Copied',
    copyFailed: 'Could not copy. The link is selected: copy it yourself.',
    print: 'Print',
    saveSvg: 'Save as SVG',
    savePng: 'Save as PNG',
    back: 'Back to campaigns',
  },
  campaign: {
    crumb: 'Campaigns',
    summary: (kind, vouchers, dates, given, used, count, top) => [`${kind} · `, { figure: count(vouchers) }, vouchers === 1 ? ' voucher · ' : ' vouchers · ', `${dates}. `, { figure: `${count(given)} given` }, ', ', { figure: `${count(used)} used` }, '.', ...(top === null ? [] : [` ${top} does most of the work.`])],
    dates: (from, until) => (until === null ? `from ${from}` : `${from} to ${until}`),
    exportCsv: 'Export CSV',
    addVoucher: 'Add a voucher',
    given: 'Given',
    givenNote: (heldBack, opened) => `${heldBack} held back · ${opened} opened`,
    used: 'Used',
    usedNote: (rate) => `${rate} of given`,
    valueGiven: 'Value given',
    valueUsed: 'Value used',
    valueNote: 'at face value',
    timeToUse: 'Time to use',
    timeNote: (cancelled, expired, median) => `${median ? 'median, at most' : 'None used yet'} · ${cancelled} cancelled · ${expired} expired`,
    noneUsed: 'None',
    minutes: (minutes) => `${minutes} min`,
    hours: (hours) => `${hours} h`,
    within: (time) => time,
    beyond: '> 30 days',
    range: (from, to) => `${from} to ${to}`,
    totalOf: (vouchers, count) => `Total · ${count(vouchers)} ${vouchers === 1 ? 'voucher' : 'vouchers'}`,
    days: (days) => `${days} days`,
    split: 'How the vouchers split',
    splitGiven: 'Given',
    splitUsed: 'Used',
    splitEmpty: 'None yet.',
    vouchersTitle: 'Each voucher',
    columns: { voucher: 'Voucher', cards: 'Cards', chance: 'Chance', given: 'Given', left: 'Left', used: 'Used', rate: 'Use rate', valueUsed: 'Value used' },
    noLimit: 'No limit',
    chance: (weight, total, percent) => `${weight} of ${total}, ${percent}`,
    total: 'Total',
    csvName: (name) => `${name} results`,
    byMedium: 'How they came',
    byMediumEmpty: 'No voucher taken through a tagged link yet.',
    qr: 'QR',
    qrTitle: (voucher) => `QR for ${voucher}`,
    qrGives: (vouchers) => (vouchers === 1 ? 'Scanning it opens the sign-up page and gives this voucher.' : "Scanning it opens the sign-up page and gives this voucher alone, not the campaign's other vouchers."),
    campaignQr: 'Campaign QR',
    campaignQrGives: (vouchers, randomOne) => (vouchers === 1 ? "Scanning it opens the sign-up page and gives the campaign's voucher." : randomOne ? `Scanning it opens the sign-up page and gives one of the campaign's ${String(vouchers)} vouchers at random, by their weights. Each voucher also has a QR of its own, in its row, that gives that voucher.` : `Scanning it opens the sign-up page and gives all ${String(vouchers)} of the campaign's vouchers. Each voucher also has a QR of its own, in its row.`),
  },
  mediums: { qr: 'QR code', link: 'Link', print: 'Printed card', station: 'Voucher station' },
  catalogue: {
    title: 'Vouchers',
    summary: (offers, given, used, count) => [{ figure: count(offers) }, offers === 1 ? ' voucher in the catalogue, ' : ' vouchers in the catalogue, ', { figure: count(given) }, ' given, ', { figure: count(used) }, ' used.'],
    newVoucher: 'New voucher',
    loading: 'Loading',
    loadFailed: 'Could not load the vouchers.',
    none: 'No vouchers yet. A new voucher goes into a campaign, or gets a QR of its own.',
    columns: { voucher: 'Voucher', given: 'Given', used: 'Used', status: 'Status' },
    active: 'In the catalogue',
    archived: 'Archived',
    revision: (revision) => `rev ${String(revision)}`,
    name: 'Name',
    nameHint: 'Members see it on the voucher, like Free 100 g bowl.',
    partOf: 'Part of a campaign',
    campaign: 'Campaign',
    campaignPick: 'Pick a campaign',
    noCampaigns: 'No open campaign takes another voucher.',
    severalTake: 'Event QR, voucher station, printed card and joining campaigns give several vouchers; an invite or audience campaign gives one only.',
    makeQr: 'Make a QR for it',
    makeQrHint: 'A campaign of its own: everyone who scans the QR gets this voucher once.',
    save: 'Save the voucher',
    saving: 'Saving',
    saved: (name) => `${name} is in the catalogue.`,
    savedIn: (name, campaign) => `${name} is in the catalogue, and ${campaign} gives it now.`,
    qrTitle: (name) => `The QR for ${name}`,
    failed: 'Could not save the voucher. Try again.',
    refused: (campaign, reason) => `The voucher is in the catalogue, but ${campaign} did not take it: ${reason}`,
    invalidName: 'Give the voucher a name.',
    invalidDays: 'Enter a whole number of days, like 30.',
    edit: (name) => `Edit ${name}`,
    editTitle: 'Edit voucher',
    editLead: (revision) => `Saving makes revision ${String(revision + 1)} of this voucher. Vouchers already given keep revision ${String(revision)} and the terms they were given with: no member's voucher changes.`,
    editTermsKept: 'Its days, hours and outlets stay as they are.',
    termsCount: (lines) => (lines === 0 ? 'No terms and conditions' : lines === 1 ? '1 term and condition' : `${String(lines)} terms and conditions`),
    editCampaigns: (names) => `Give the new terms in the campaigns that give it now: ${names}`,
    editCampaignsHint: (revision) => `Left unticked, those campaigns keep giving revision ${String(revision)} until they are set again.`,
    picture: 'Picture',
    pictureHint: 'A PNG or JPEG up to 2 MB, square works best. Every voucher of this offer shows it on the member page, those already given too. It saves at once, with no new revision.',
    picturePick: 'Add a picture',
    pictureChange: 'Change picture',
    pictureRemove: 'Remove',
    pictureUploading: 'Uploading',
    pictureTooBig: 'That picture is over 2 MB. Pick a smaller one.',
    pictureWrongType: 'Pick a PNG or JPEG picture.',
    pictureFailed: 'The picture could not be saved. Try again.',
    editSave: 'Save new revision',
    editSaved: (name, revision) => `${name} is now at revision ${String(revision)}.`,
    editSavedCampaigns: (count) => (count === 1 ? '1 campaign gives the new terms from now on.' : `${String(count)} campaigns give the new terms from now on.`),
  },
  voucher: {
    off: 'off',
    offUpTo: (cap) => `off, up to ${cap}`,
    free: 'Free',
    grams: (grams) => `${String(grams)}\u00a0g`,
    everyDay: 'Every day',
    anyTime: 'Any day, any time',
    day: { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' },
    from: (date) => `From ${date}`,
    until: (date) => `Until ${date}`,
    noEnd: 'No end date',
    goodFor: (days) => (days === 1 ? 'Good for 1 day' : `Good for ${String(days)} days`),
    foodOnly: 'Food only, not drinks',
    minSpend: (amount) => `Spend at least ${amount}`,
    minGrams: (grams) => `Order at least ${String(grams)}\u00a0g`,
    conditions: 'Terms and conditions',
    onePerBill: 'One voucher per bill',
    onlyAt: (outlets) => `At ${outlets.join(', ')} only`,
    brandOnly: (brands) => `At ${brands.join(', ')} outlets only`,
    status: { usable: 'Ready to use', redeemed: 'Used', revoked: 'Withdrawn', lapsed: 'Ended unused' },
  },
};

const zh: CrmStrings = {
  join: {
    brandSub: '会员',
    title: (name) => (name === null ? '加入会员' : `加入 ${name} 会员`),
    lead: '用手机号码注册，领取你的优惠券。',
    phoneLabel: '手机号码',
    phoneHint: (example) => (example === null ? '先选国家，再输入手机号码。' : `先选国家，再输入手机号码，例如 ${example}。`),
    phoneInvalid: (example) => (example === null ? '请输入所选国家的手机号码。' : `请输入手机号码，例如 ${example}。`),
    sendCode: '发送验证码',
    sending: '发送中',
    tooManyCodes: (minutes) => (minutes === null ? '这个号码收到的验证码太多了。请几分钟后再试。' : `这个号码收到的验证码太多了。请${String(minutes)}分钟后再试。`),
    codeBudgetSpent: '今天的注册验证码已暂停发送。请询问店员，或明天再试。',
    voucherGone: '这个二维码的优惠券已不再发放。请向店员索取最新的二维码。',
    codeTitle: '请查看手机短信',
    codeSentTo: (phone) => `我们已通过 SMS 发送 6 位数验证码到 ${phone}。`,
    changeNumber: '更换号码',
    codeLabel: (phone) => `我们发送到 ${phone} 的 6 位数验证码`,
    codeInvalid: '请输入 SMS 里的 6 位数验证码。',
    codeWrong: (triesLeft) => (triesLeft > 0 ? `验证码不正确。还可以试 ${String(triesLeft)} 次。` : '验证码不正确。'),
    resend: '重新发送验证码',
    resendIn: (seconds) => `${String(seconds)} 秒后可重新发送`,
    resent: '新验证码已发送，旧的验证码已失效。',
    notDelivered: '验证码未能发送，请重试。',
    codeExpired: '验证码已过期。请重新发送。',
    tooManyAttempts: '输错太多次了。请重新发送验证码。',
    noCodeSent: '验证码还没收到。如果一直没收到，请重新发送。',
    startAgain: '重新开始',
    challengeGone: '这次注册已完成或已超时。请重新开始。',
    noticeOutdated: '这个页面已更新。请重新载入，查看最新的隐私声明。',
    reload: '重新载入',
    detailsHeading: '你的资料',
    nameLabel: '名字',
    nameRequired: '请输入你的名字。',
    genderLabel: '性别',
    genders: { female: '女', male: '男' },
    genderRequired: '请选择一项。',
    raceLabel: '种族',
    races: { malay: '马来人', chinese: '华人', indian: '印度人', others: '其他' },
    raceRequired: '请选择一项。',
    year: '年',
    birthdayLabel: '生日',
    inviteLabel: '推荐码',
    inviteHint: '如果有会员给了你推荐码。',
    inviteInvalid: '推荐码是 8 个英文字母和数字，例如 7KQ2M9XA。',
    inviteUnknown: '没有会员用这个推荐码。请检查一下，或留空。',
    day: '日',
    month: '月',
    months: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
    monthsShort: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
    birthdayIncomplete: '请选择你出生的日、月和年份。',
    birthdayNoSuchDate: '这个月没有这一天。',
    controllerUnknown: '本商家',
    noticeHeading: '隐私声明',
    noticeDraftBadge: (version, controller) => `${controller} 的草稿，非最终版本。版本 ${version}`,
    notice: (controller) => [
      `${controller} 会保存你的手机号码、名字、性别、生日（含年份）和种族，以及你的到店和优惠券记录，用于管理你的会员资格、规划会员优惠，并发送你同意接收的优惠。`,
      '资料由为我们运行系统的公司保管，存放在新加坡的服务器上，绝不出售。',
      `你可以随时要求 ${controller} 查看、更正或删除你的资料。`,
    ],
    noticeFull: '阅读完整的隐私声明',
    marketingHeading: '优惠与最新消息',
    marketingLead: (controller) => `选择 ${controller} 可以用什么方式发优惠给你。之后可以更改。`,
    marketingWhatsApp: '通过 WhatsApp 收优惠',
    marketingSms: '通过 SMS 收优惠',
    marketingAnswers: { yes: '要', no: '不要' },
    marketingRequired: '请选择要或不要。',
    agreement: '加入即表示你已阅读隐私声明。',
    submit: '加入',
    submitting: '加入中',
    erasedTitle: '这个号码不能再加入',
    erasedBody: '这个号码以前是会员，资料已被删除。迎新优惠券每个手机号码只限一张，所以这个号码不能再领取。',
    linkInvalidTitle: '这个链接无效',
    linkInvalidBody: '请向店家索取新的链接。',
    linkInvalid: '这个链接无效，请向店家索取新的链接。',
  },
  privacy: {
    title: '隐私声明',
    lead: (controller) => `${controller}（[注册公司名称及编号]）根据马来西亚《2010年个人资料保护法令》向你发出本声明，说明你加入会员时我们收集什么、为什么收集，以及你可以怎么做。`,
    sections: (controller) => [
      {
        heading: '我们收集什么',
        paragraphs: [
          '加入时：你的手机号码、名字、性别、生日（含年份）和种族。我们会发送 SMS 一次性验证码到你的号码，以核实号码。',
          '我们也会记录你加入时页面所用的语言、你通过哪个链接或二维码加入、你输入的邀请码，以及你是否同意通过 WhatsApp 和 SMS 接收优惠。',
          '使用会员期间：你获得和使用的优惠券、每次店员在柜台找到你的到店记录（含分店和时间），以及通过你的邀请链接加入的朋友。',
          '我们不会询问你的电邮、身份证号码、地址或宗教，也不收集任何敏感个人资料，例如健康状况、政治或宗教信仰。',
        ],
      },
      {
        heading: '我们为什么使用',
        paragraphs: [
          '管理你的会员资格：让你登录、发给你优惠券、在柜台核对优惠券，并确保每个手机号码只能领取一次迎新优惠券。',
          '按年龄、性别和种族了解会员的整体情况，以便规划菜单、分店和优惠。',
          '根据你的到店和优惠券记录，挑选适合你的优惠，例如生日优惠或常客优惠。',
          '只在你同意的渠道（WhatsApp 或 SMS）发送优惠给你，并使用你加入时的语言。',
          '保存法律要求的记录，并发现和防止优惠券被滥用。',
        ],
      },
      {
        heading: '我们与谁分享',
        paragraphs: [
          '我们绝不出售你的资料。我们只与根据合约为我们提供服务的公司分享，每家公司只获得它需要的资料：我们会员系统的供应商；Google Cloud，在新加坡托管系统，因此你的资料存放在马来西亚境外；发送一次性验证码和 SMS 优惠的 SMS 供应商；以及 WhatsApp（Meta），仅在你同意通过 WhatsApp 接收优惠时。',
          '只有在法律要求时，我们才会把你的资料交给有关当局。',
        ],
      },
      {
        heading: '我们保存多久',
        paragraphs: [
          '你是会员期间，我们会保存你的资料。你要求删除时，我们会删除你的手机号码、名字、性别、生日和种族，只保留由你的号码生成的单向代码。这个代码无法还原成号码，只用来确保同一个号码不能再领取迎新优惠券。',
          '你的到店和优惠券记录会保留，但不再与任何名字或号码相连，用来统计优惠的成效。系统中每个请求的日志不含个人资料，3 年后删除。',
        ],
      },
      {
        heading: '你的选择和权利',
        paragraphs: [
          '除了邀请码，表格上每个问题都必须回答才能加入。如果留空，就无法加入。',
          'WhatsApp 和 SMS 优惠由你分别选择，选择不要也不影响加入。你可以随时告诉任何分店的店员或写信给我们，停止接收任一渠道的优惠。',
          `你可以要求 ${controller} 让你查看、更正、提供副本或删除你的资料，也可以随时撤回你的同意。请按以下地址联系我们，我们会在 21 天内回复。撤回保存资料的同意，即表示结束你的会员资格。`,
          '你也可以向个人资料保护局（JPDP）投诉。',
        ],
      },
      {
        heading: '如何联系我们',
        paragraphs: [
          `${controller}，[注册公司名称及编号]，[邮寄地址]。`,
          '资料保护官：[姓名]，[电邮]，[电话号码]。',
          '我们可能会更新本声明；你加入时同意的版本会被记录下来。本声明有英文、马来文和中文版本；如有不同，以英文版本为准。最后更新：2026年10月29日。',
        ],
      },
    ],
  },
  members: {
    title: '会员',
    codesPaused: (budget) => `今天 ${budget} 条登录验证码的上限已用完。新的注册验证码暂停到明天，或直到您提高上限。`,
    codesNotDelivered: (failed, tried) => `今天 ${tried} 条登录验证码中有 ${failed} 条未能发送。顾客可以立即重新索取验证码。如果这个数字持续上升，短信服务可能已中断。`,
    undelivered: {
      open: '查看是哪些注册',
      title: '今天未送达的验证码',
      lead: '今天验证码未能发送的每个注册，及其最近一次失败。电话号码部分隐藏。',
      columns: { phone: '电话', time: '时间', reason: '原因', source: '来源', since: '之后' },
      reasons: {
        'gateway down': '短信服务中断',
        'number rejected': '短信服务拒绝了这个号码',
        'channel not configured': '短信发送尚未设置',
        'timed out': '短信服务没有回应',
        'no phone on record': '没有记录电话号码',
      },
      since: { joined: '已加入', gotCode: '之后收到了验证码', noCode: '此后未收到验证码' },
      noPhone: '已不再保留',
      more: (count) => `还有 ${count} 个`,
    },
    refresh: '刷新',
    refreshing: '刷新中',
    totalMembers: '会员总数',
    totalErased: (count) => `（${count} 位已删除）`,
    bySource: '按来源',
    byDate: '按加入日期',
    funnel: '注册情况',
    funnelStarted: '索取验证码',
    funnelSent: '验证码已发送',
    funnelJoined: '已加入',
    funnelNotDelivered: '验证码未送达',
    funnelWrongCodes: '输错验证码',
    noMembers: '还没有会员。有人加入后就会显示在这里。',
    noData: '暂无资料',
    columns: { member: '会员', source: '来源', whatsApp: 'WhatsApp', sms: 'SMS', visits: '到店次数', lastVisit: '最近到店', since: '距上次到店', mostVisited: '最常到店', race: '种族', gender: '性别', age: '年龄' },
    never: '从未',
    noValue: '-',
    sinceDays: (days, count) => (days <= 0 ? '今天' : `${count(days)} 天`),
    unknownOutlet: '未知分店',
    revealing: '加载中',
    revealFailed: '无法加载',
    summary: (total, week, count) => ['共 ', { figure: count(total) }, ' 位会员，最近 7 天加入 ', { figure: count(week) }, ' 位。'],
    kpiWeek: '最近 7 天加入',
    kpiWeekNote: (from) => `自 ${from} 起`,
    kpiConversion: '注册转化率',
    kpiConversionNote: (joined, asked) => `${asked} 位索取验证码，${joined} 位已加入`,
    kpiConsent: '同意接收优惠',
    kpiConsentNote: (shown) => `WhatsApp 或 SMS，共显示 ${shown} 位`,
    search: '搜索名字、电话、会员号或来源',
    numbered: (number) => `会员号 ${number}`,
    sourceFilter: '来源',
    allSources: '所有来源',
    filters: {
      label: '筛选',
      clear: '清除筛选',
      done: (shown) => `显示 ${shown} 位`,
      medium: '来源方式',
      allMediums: '所有方式',
      noMedium: '无标记',
      country: '国家',
      allCountries: '所有国家',
      gender: '性别',
      allGenders: '所有性别',
      age: '年龄',
      allAges: '所有年龄',
      band: (from, to) => (from === 0 && to !== null ? `${String(to + 1)} 岁以下` : to === null ? `${String(from)} 岁以上` : `${String(from)} 至 ${String(to)} 岁`),
      birthMonth: '生日月份',
      allMonths: '所有月份',
      joinedFrom: '加入日期从',
      joinedUntil: '加入日期至',
      notGiven: '未提供',
      lastVisit: '最近到访',
      lastOutlet: '最近分店',
      spend: '累计消费',
      soonLead: '需要自助点餐机的账单',
    },
    export: {
      label: '导出 Excel',
      busy: '导出中',
      failed: '无法导出会员。请再试一次。',
      fileName: (date) => `会员 ${date}`,
      columns: { memberNumber: '会员号', name: '名字', phone: '电话', gender: '性别', yearOfBirth: '出生年份', birthMonth: '生日月份', country: '国家', source: '来源', medium: '来源方式', utmSource: 'UTM 来源', utmCampaign: 'UTM 活动', joined: '加入日期', whatsApp: 'WhatsApp 优惠', sms: 'SMS 优惠', vouchers: '优惠券', used: '已使用', visits: '到店次数', lastVisit: '最近到店', lastOutlet: '最近到店分店', daysSince: '距上次到店天数', mostVisited: '最常到店分店', race: '种族' },
    },
    showing: (shown, loaded) => `显示 ${shown} / ${loaded} 位`,
    noMatch: '没有符合的会员。请更改搜索，或显示更多会员。',
    open: (name) => `打开 ${name}`,
    erasedOn: (date) => `${date} 已删除`,
    sheet: {
      profile: '个人资料',
      memberNumber: '会员号',
      phone: '电话',
      gender: '性别',
      yearOfBirth: '出生年份',
      race: '种族',
      country: '国家',
      medium: '来源方式',
      campaign: 'UTM 活动',
      name: '名字',
      source: '来源',
      joined: '加入日期',
      offers: '优惠通知',
      yes: '是',
      no: '否',
      vouchers: '优惠券',
      vouchersCount: (given, used) => `持有 ${String(given)} 张 · 已使用 ${String(used)} 张`,
      vouchersLoading: '正在载入优惠券',
      vouchersFailed: '无法载入优惠券。',
      noVouchers: '还没有优惠券。',
      points: '积分',
      pointsLead: '每次消费赚取的积分，以及可用余额。',
      tier: '等级',
      tierLead: '会员的等级和可享有的福利。',
      visits: '到店记录',
      visitsCount: (visits, count) => `${count(visits)} 次到店`,
      lastVisit: '最近到店',
      mostVisited: '最常到店',
      noVisits: '还没有到店记录。在收银站扫描会员二维码即算一次到店。',
    },
    noName: '未提供',
    erased: '已删除',
    showMore: '显示更多',
    loading: '载入中',
    loadFailed: '无法载入会员。',
    retry: '再试一次',
    shareTitle: '注册链接',
    shareLead: '为海报、二维码或活动制作链接。通过这个链接加入的会员都会带上它的来源标签。',
    shareTagLabel: '来源标签',
    shareTagHint: '链接用在哪里，例如 hoarding 或 poster-a。',
    shareTagInvalid: '只能用小写英文字母、数字和横线，最多 40 个，例如 poster-a。',
    shareEmpty: '输入来源标签就能拿到链接。',
    shareCopy: '复制链接',
    shareCopied: '已复制',
    shareCopyFailed: '无法复制。链接已选取，请自行复制。',
    shareQrLabel: (link) => `${link} 的二维码`,
    shareQrHint: '用手机相机扫描即可打开链接。',
  },
  channels: {
    whatsApp: 'WhatsApp',
    sms: 'SMS',
  },
  member: {
    title: '我的会员',
    welcomeJoined: (name) => (name === null ? '欢迎！你已成为会员' : `欢迎加入 ${name} 会员`),
    welcomeBack: '欢迎回来',
    qrTitle: '我的会员二维码',
    qrLabel: '我的会员二维码',
    qrHint: '在柜台扫描即可使用优惠券',
    vouchersTitle: '我的优惠券',
    loading: '正在载入你的优惠券',
    loadFailed: '无法载入你的优惠券。',
    noVouchers: '目前没有优惠券。',
    endedTitle: (count) => `已使用和已结束（${String(count)}）`,
    entered: '你扫描的二维码优惠券已经是你的了。',
    cardLimit: '这个活动的卡你已领满，每位会员有上限。这张卡不会被使用。',
    enterRefused: {
      'not a member': '这部手机还没有登录会员。',
      'member erased': '这个会员已被删除。',
      'offer not in campaign': '这个二维码的优惠券已不再发放。',
      'already entered': '你已经领过这个二维码的优惠券了。',
      'campaign not open': '这个二维码的优惠券目前不能领取。',
      'campaign ended': '这个二维码的优惠券已结束。',
      'campaign full': '这个二维码的优惠券已经全部送完。',
      'over budget': '这个二维码的优惠券已经全部送完。',
      'draw used': '这个二维码已经用过了。',
      'card used': '这张卡已经用过了。',
      'card void': '这张卡已失效。',
    },
    drawRefused: {
      'not a member': '这部手机还没有登录会员。',
      'member erased': '这个会员已被删除。',
      'already entered': '你已经领过这次抽奖的奖励了。每人限领一次。',
      'draw used': '这个二维码已经用过了。请再按一次按钮拿新的。',
      'card used': '这张卡已经用过了。',
      'card void': '这张卡已失效。',
      'campaign not open': '抽奖目前还没开始。',
      'campaign ended': '这次抽奖已结束。',
      'campaign full': '这次抽奖的奖励已经全部送完。',
      'over budget': '这次抽奖的奖励已经全部送完。',
    },
    withheld: {
      'campaign not open': '你扫描的二维码优惠券目前不能领取。',
      'campaign ended': '你扫描的二维码优惠券已结束。',
      'campaign full': '你扫描的二维码优惠券已经全部送完。',
      'over budget': '你扫描的二维码优惠券已经全部送完。',
      'already entered': '你已经领过这次抽奖的奖励了。每人限领一次。',
      'draw used': '这个二维码已经用过了，所以没有奖励。请再按一次按钮拿新的。',
      'card used': '这张卡已经用过了，所以没有奖励。',
      'card void': '这张卡已失效，所以没有奖励。',
    },
    inviteTitle: '推荐朋友',
    inviteCodeLabel: '我的推荐码',
    inviteLead: '分享你的链接，或把推荐码告诉朋友，让他们加入时输入。推荐奖励会出现在你的优惠券里。',
    inviteCount: (joined, rewards) => `${String(joined)} 位朋友已加入 · ${String(rewards)} 个奖励`,
    inviteWhatsApp: '分享到 WhatsApp',
    inviteCopy: '复制我的链接',
    inviteCopyCode: '复制我的推荐码',
    inviteCodeCopied: '推荐码已复制',
    inviteMessage: (link, code, name) => `${name === null ? '加入会员' : `加入 ${name} 会员`}，用我的链接注册就能领迎新优惠券：${link}\n\n或在注册时输入我的推荐码 ${code}。`,
    notYou: '不是你？',
    signOut: '从这部手机登出',
    replaced: '你的会员二维码已转到另一部手机。请重新登录才能在这里使用。',
    signInTitle: '我的会员',
    signInLead: '用手机号码登录，查看你的会员二维码和优惠券。',
    signIn: '登录',
    signingIn: '登录中',
    notMemberTitle: '这个号码还不是会员',
    notMemberBody: '用这个号码加入，就能领取优惠券。',
    joinNow: '马上加入',
    standingTitle: '我的等级和现金回馈',
    tierLabel: '等级',
    tierNote: (earned, from, next) => `${earned} / ${from} 升级至 ${next}`,
    tierTo: (next) => ['升级至 ', { figure: next }],
    tierCount: (earned, from) => [{ figure: earned }, ` / ${from} 积分`],
    greeting: { morning: '早上好，', afternoon: '下午好，', evening: '晚上好，' },
    inviteHeadline: '好吃的，和朋友一起分享',
    inviteBlurb: (name) => `邀请朋友加入${name === null ? '我们' : ` ${name}`}，你们都能获得奖励。`,
    tierTop: '最高等级',
    cashbackLabel: '现金回馈',
    cashbackPoints: (points, format) => `${format(points)} 积分`,
    cashbackInfoLabel: '现金回馈怎么算',
    cashbackInfo: (spent, points, format) => `消费 ${spent} = ${format(points)} 积分`,
    memberNumber: '会员号',
    cardRow: '我的会员卡',
    cardRowSub: '放大会员二维码，方便柜台扫描',
    cardLarge: '我的会员二维码（大）',
    vouchersRowSub: (usable) => `${String(usable)} 张可用`,
    storeRow: '积分商城',
    storeRowSub: '用积分兑换奖励',
    qrShow: '出示我的会员二维码',
    ready: (count, format) => `${format(count)} 张可用`,
    fromReferral: '感谢你推荐朋友',
    fromReferred: '凭朋友的邀请加入所得',
    more: '会员更多服务',
  },
  lookup: {
    title: '查找会员',
    phoneLabel: '手机号码或会员号',
    phoneHint: ({ example, abroadExample }) => `手机号码如 ${example} 或 ${abroadExample}，或会员号如 100023`,
    phoneInvalid: ({ example, abroadExample }) => `请输入电话号码，例如 ${example} 或 ${abroadExample}，或会员号，例如 100023。`,
    find: '查找',
    finding: '查找中',
    notFound: '没有会员用这个号码。',
    erased: '这个会员已被删除。',
    joined: (date, source) => `${date} 通过 ${source} 加入`,
    memberNumber: (number) => `会员号 ${number}`,
    noName: '未提供名字',
    vouchers: '优惠券',
    noVouchers: '还没有优惠券。',
    clear: '清除',
    failed: '找不到会员。请再试一次。',
  },
  redeem: {
    title: '兑换',
    outletLabel: '分店',
    outletPick: '选择分店',
    outletMissing: '请选择这部设备所在的分店。',
    noOutlets: '这个集团还没有分店。请经理先添加。',
    setupTitle: '设置这个收银台',
    setupLead: '选择这部设备所在的分店，只需设置一次。之后员工只需在这里扫描和兑换，每次兑换都会记录这个分店。',
    stationName: '收银台名称',
    stationNameHint: '例如“前台收银”。',
    stationNameMissing: '请为这个收银台命名，例如“前台收银”。',
    setupSubmit: '在这里使用这部设备',
    settingUp: '设置中',
    setupCancel: '取消',
    setupFailed: '无法设置这部设备。请再试一次。',
    stationAt: (outlet, station) => `${outlet} · ${station}`,
    change: '更改',
    stationGone: '这部设备已不是收银台。请重新设置。',
    scanTitle: '扫描会员二维码',
    scanLead: '会员打开会员页面，出示二维码。',
    startCamera: '用相机扫描',
    stopCamera: '关闭相机',
    cameraUnavailable: '相机无法使用。请用扫描器，或允许这个页面使用相机。',
    passLabel: '会员二维码',
    passHint: '二维码扫描器会在这里输入。',
    find: '查找会员',
    finding: '正在查找会员',
    unknownPass: '这不是会员二维码，或已转到另一部手机。请会员重新打开会员页面。',
    memberTitle: (who) => `会员：${who}`,
    noName: '未提供名字',
    noVouchers: '这位会员没有可用的优惠券。',
    choose: '要使用的优惠券',
    redeem: (foodOnly) => (foodOnly === null ? '兑换' : foodOnly ? '兑换 · 只限食物，每单一张' : '兑换 · 每单一张'),
    redeeming: '兑换中',
    redeemed: '已兑换',
    redeemedAt: (outlet, time) => `${outlet}，${time}`,
    nextCustomer: '下一位顾客',
    askManager: '这部设备还不是收银台。请经理在这里登录一次，为分店设置好。',
    loadFailed: '无法载入分店。',
    refused: {
      'scan the member QR again': '请重新扫描会员二维码：扫描已超过 15 分钟，或是在另一个收银台扫描的。',
      'one voucher per visit': '这次消费已经用过优惠券了。每次消费限用一张。',
      "not this member's voucher": '这张优惠券属于另一位会员。',
      'already redeemed': '这张优惠券已经用过了。',
      revoked: '这张优惠券已被收回。',
      'not valid yet': '这张优惠券还不能使用。',
      expired: '这张优惠券已结束。',
      'outside its days and hours': '这张优惠券现在不能使用。请查看可用的日子和时间。',
      'another outlet': '这张优惠券适用于其他分店。请查看可使用的分店。',
      'another brand': '这张优惠券只适用于其他品牌的分店。',
    },
  },
  campaigns: {
    title: '活动',
    summary: (open, given, used, count) => [{ figure: count(open) }, ' 个活动进行中，已发出 ', { figure: count(given) }, ' 张优惠券，已使用 ', { figure: count(used) }, ' 张。'],
    details: '详情',
    live: (count, format) => `${format(count)} 个进行中`,
    newCampaign: '新活动',
    loading: '载入中',
    loadFailed: '无法载入活动。',
    retry: '再试一次',
    none: '还没有活动。',
    noOnJoin: '新会员没有优惠券：目前没有进行中的活动送迎新优惠券。',
    managersOnly: '活动和成效只限经理查看。',
    makeOnJoin: '制作迎新优惠券',
    open: '进行中',
    ended: '已结束',
    starts: (date) => `${date} 开始`,
    kind: { 'on join': '加入即送', 'by link': '活动二维码', 'by draw': '领券机抽奖', 'by card': '印刷卡', referral: '推荐朋友', 'to audience': '指定客群' },
    gives: (vouchers, rule) => (vouchers.length === 1 ? (vouchers[0] ?? '') : rule === 'random one' ? `从 ${vouchers.join('、')} 中随机送一张` : vouchers.join(' + ')),
    givesPerCard: (vouchers) => (vouchers.length === 1 ? (vouchers[0] ?? '') : `每张卡一种：${vouchers.join('、')}`),
    drawn: (pressed, claimed, count) => `领券机被按了 ${count(pressed)} 次，已领取 ${count(claimed)} 张。`,
    station: '领券机',
    taken: '张已领取',
    and: '，',
    used: (rate) => `张已使用，占已领取的 ${rate}。`,
    valueUsed: '面值已被使用。',
    endedFold: (ended, count) => `${count(ended)} 个已结束的活动`,
    given: (given, count) => `已发出：${given.map((one) => `${one.what} × ${count(one.count)}`).join('、')}`,
    usedSplit: (used, count) => `已使用：${used.map((one) => `${one.what} × ${count(one.count)}`).join('、')}`,
    turnedAway: (count) => `${String(count)} 次未能领取：已送完、超出预算或已关闭`,
    cardsTurnedAway: (count) => `${String(count)} 次因卡已用过或已作废而未能领取`,
    lift: (treated, holdout) => `回头率：收到优惠券的 ${treated}，对照组 ${holdout}`,
    showQr: '二维码和链接',
    cards: '卡片和成效',
    end: '结束',
    endConfirm: '马上结束',
    ending: '结束中',
    keep: '继续进行',
    endFailed: '无法结束活动。请再试一次。',
  },
  eventQr: {
    title: '新活动',
    lead: {
      'by link': '扫描这个二维码的人会加入或登录会员，并领取一次优惠券。',
      'by draw': '分店的领券机每按一次就显示新的二维码：扫描的人会加入或登录会员，领取抽到的奖励，每人限领一次。',
      'by card': '印刷卡片后亲手派发，也可以随机派发。每张卡都有自己的二维码，只能用一次：扫描的人会加入或登录会员，并领取这批卡的优惠券。',
      'on join': '每个加入的人都会收到这张优惠券，除非是通过其他活动的二维码加入的。',
      referral: '会员分享自己的链接或推荐码。朋友用它加入后就会和会员绑定，并按你设定的时间和对象送出这张优惠券。',
    },
    name: '名称',
    nameHint: '会员会在优惠券上看到，例如“TRX 开幕周”。',
    gives: '会员怎样领取',
    givesBy: { 'by link': '扫描活动二维码', 'by draw': '在领券机按一下', 'by card': '印刷卡', 'on join': '每个加入的人', referral: '推荐朋友' },
    rewardN: (n) => `优惠券 ${String(n)}`,
    addReward: '再加一张优惠券',
    remove: '删除',
    removeReward: (title) => `删除 ${title}`,
    rule: '每位会员获得',
    rules: { all: '每一张', 'random one': '按权重随机一张' },
    ruleHint: '每一张：每个人领取全部优惠券。随机一张：每次扫描领取一张，权重 3 的优惠券出现的机会是权重 1 的三倍。如果要亲手随机派发卡片，请在上面选择印刷卡。',
    weight: '权重',
    weightHint: (chance) => (chance === null ? '它的机会是它的权重占全部权重的比例。' : `它的机会：${chance}。`),
    quantity: '发出数量',
    quantityHint: '发满这个数量后，活动就不再发这张优惠券。不填即不限。',
    benefit: '优惠券',
    benefitKind: '优惠内容',
    benefits: (currency) => ({ 'amount off': `减 ${currency}`, 'percent off': '打折 (%)', 'free item': '免费单品', 'free grams': '免费克数' }),
    amount: (currency) => `减免金额 (${currency})`,
    percent: '折扣 (%)',
    percentCap: (currency) => `最高减免 (${currency})`,
    item: '免费送的单品',
    grams: '免费送的克数',
    days: '可使用的日子',
    validDays: '有效天数',
    validDaysHint: '从会员领取当天算起；如果开始使用日期较晚，就从那天算起。',
    foodOnly: '只限食物，不包括饮料',
    more: '更多条款',
    hours: '可使用的时间',
    hoursFrom: '开始时间',
    hoursUntil: '结束时间',
    minSpend: (currency) => `最低消费 (${currency})`,
    minGrams: '最低点餐 (克)',
    startsOn: '开始使用日期',
    startsOnHint: '会员可以提早领取，例如从围板宣传到开幕期间。',
    outlets: '可使用的分店',
    outletsHint: '全部不勾选即适用于所有分店。',
    brands: '适用品牌',
    brandsHint: '在该品牌的分店使用，按该品牌的条款。全部不勾选即适用于所有品牌。',
    giveFrom: '派送开始',
    giveUntil: '派送结束',
    cap: '最多派送张数',
    capHint: '派完后再扫描会显示已送完。',
    budget: (currency) => `预算 (${currency})`,
    budgetHint: '最多派送的面值总额，按优惠券被领取时计算。',
    source: '链接名称',
    sourceHint: '通过这个二维码加入的会员会带上这个名称。',
    alsoOn: '也送给通过以下方式加入的人',
    alsoOnHint: '在海报或页面注册的人都会收到。如果某个方式只想送它自己的优惠券，就取消勾选。',
    routes: { 'event QR': '活动二维码', draw: '领券机抽奖', 'printed card': '印刷卡', referral: '朋友推荐' },
    referralWhen: '何时送出',
    referralWhenBy: { 'on sign-up': '朋友加入时', 'on first redemption': '朋友第一次使用优惠券时' },
    referralTo: '谁获得',
    referralToBy: { both: '双方', inviter: '推荐的会员', invitee: '朋友' },
    perInviter: '每位会员最多奖励次数',
    perInviterHint: '超过次数后，朋友仍可通过推荐加入，但会员不会再获得奖励。',
    cardCount: '卡片数量',
    cardLabel: '标签',
    cardLabelHint: '例如机器或口味。',
    serials: '卡号',
    serialsPreview: (total, first, last, count) => `${count(total)} 张卡，${first} 至 ${last}`,
    prefix: '开头',
    prefixHint: '例如 KM-TRX-',
    digits: '位数',
    firstSerial: '起始号码',
    perMember: '每位会员可领卡数',
    perMemberHint: '留空即不限。',
    optional: '选填',
    submit: { 'by link': '制作二维码', 'by draw': '制作抽奖', 'by card': '制作卡片', 'on join': '开始派送', referral: '开始推荐活动' },
    submitting: { 'by link': '正在制作二维码', 'by draw': '正在制作抽奖', 'by card': '正在制作卡片', 'on join': '正在开始', referral: '正在开始推荐活动' },
    cancel: '取消',
    invalid: {
      name: '请为活动命名。',
      amount: (currency) => `请输入减免金额 (${currency})，例如 5 或 2.50。`,
      percent: '请输入 1 到 100 的百分比。',
      item: '请填写免费单品的名称。',
      grams: '请输入免费克数，例如 100。',
      days: '请至少选一天。',
      validDays: '请输入天数，例如 7。',
      hours: '请填写两个时间，结束时间要在开始时间之后。',
      whole: '请输入大于零的数字。',
      until: '派送结束日期早于开始日期。',
      source: '只能用小写英文字母、数字和横线，最多 40 个。',
      cards: '请输入卡片数量，例如 250。',
      perInviter: '请输入 1 到 100 的数字。',
      tooMany: (max, count) => `每批最多印 ${count(max)} 张卡。`,
      prefix: '只能用大写英文字母、数字和横线，例如 KM-TRX-。',
      digits: '请输入 1 到 8 位数。',
      moreDigits: '最后一张卡号需要更多位数。',
      first: '请输入起始卡号，例如 1。',
      weight: (max) => `请输入 1 到 ${String(max)} 的整数。`,
      quantity: (max, count) => `请输入 1 到 ${count(max)} 的整数，或留空。`,
    },
    refused: {
      'source in use': '另一个活动已在用这个链接名称。请在“更多条款”里更改。',
      'ends before it starts': '派送结束日期早于开始日期。',
      'referral campaign open': '另一个推荐活动正在进行。请先结束它：一次只能进行一个。',
    },
    failed: { 'by link': '无法制作二维码。请再试一次。', 'by draw': '无法制作抽奖。请再试一次。', 'by card': '无法制作卡片。请再试一次。', 'on join': '无法开始派送。请再试一次。', referral: '无法开始推荐活动。请再试一次。' },
  },
  voucherStation: {
    title: '领取优惠券',
    brandSub: '优惠券',
    pressTitle: '领取优惠券',
    pressLead: '按下按钮，再用手机相机扫描二维码。',
    press: '按这里领优惠券',
    drawing: '抽奖中',
    scanTitle: '用手机扫描',
    scanLead: '打开相机对准二维码。只能用一次，限一人。',
    qrLabel: '你的优惠券二维码',
    left: (seconds) => `这个二维码 ${String(seconds)} 秒后消失`,
    done: '完成',
    askStaffTitle: '请找员工帮忙',
    offline: '这台机器暂时连不上系统。员工可以帮你。',
    closed: '这台机器目前没有优惠券。员工可以帮你。',
    tryAgain: '再试一次',
    unsetTitle: '这部设备还不是领券机',
    unsetLead: '请经理在这里登录，为抽奖设置好。',
    signIn: '登录以设置',
    signInAsManager: '以经理身份登录',
    setupTitle: '设置这台领券机',
    setupLead: '选择这部设备摆放的位置和它送出的抽奖。设置后会自动登出，设备会自己运行。',
    campaign: '抽奖',
    campaignPick: '选择抽奖',
    campaignMissing: '请选择这台领券机送出的抽奖。',
    noDraws: '目前没有进行中的抽奖。请先在活动页面制作一个。',
    name: '领券机名称',
    nameHint: '例如“入口”。',
    nameMissing: '请为这台领券机命名，例如“入口”。',
    setupSubmit: '设为领券机',
    setupFailed: '无法设置这部设备。请再试一次。',
  },
  stationLink: {
    lead: '在分店的设备上以经理身份登录，打开这个链接：它就会成为这个抽奖的领券机，顾客只需按一个按钮。',
  },
  defaultTerms: {
    open: '默认条款',
    title: '新优惠券的默认条款',
    lead: '新的优惠券会先带上这些条款，你可以为那张优惠券另行修改。在这里保存只会填入新的优惠券，不会改动已经制作的优惠券。',
    leadBrands: '新的优惠券会先带上这些条款：指定品牌的优惠券用该品牌的条款，否则用适用所有品牌的条款。你可以为每张优惠券另行修改。在这里保存只会填入新的优惠券，不会改动已经制作的优惠券。',
    scope: '默认条款适用于',
    everyBrand: '所有品牌',
    label: '默认条款',
    save: '保存默认条款',
    saving: '保存中',
    saved: '已保存。新的优惠券会先带上这些条款。',
    loadFailed: '无法载入默认条款。',
    failed: '无法保存默认条款。请再试一次。',
  },
  terms: {
    legend: '条款与条件',
    hintNew: '会员会在这张优惠券上看到这些条款。它们先填入你的默认条款，可以为这张优惠券随意修改。',
    hintEdit: '会员会在这张优惠券上看到这些条款。已发出的优惠券保留原本的条款。',
    line: (n) => `第 ${String(n)} 条`,
    remove: (n) => `删除第 ${String(n)} 条`,
    add: '添加一条',
  },
  printRuns: {
    lead: '每张卡都有自己的二维码，只能用一次。把文件发给印刷商，或在这里印出二维码页。',
    loading: '载入中',
    loadFailed: '无法载入卡片。',
    none: '还没有制作卡片。',
    serials: (first, last) => (first === last ? first : `${first} 至 ${last}`),
    made: (date) => `${date} 制作`,
    cards: '卡片',
    reward: '优惠券',
    scanned: '已扫描',
    claimed: '已领取',
    used: '已使用',
    voided: '已作废',
    download: '下载印刷文件',
    linksTo: (host) => `卡片的二维码会打开 ${host}。`,
    notPublic: (host) => `卡片的二维码会打开 ${host}，但顾客的手机无法连上这个地址。请在公开网址下载印刷文件。`,
    csvHeader: ['serial', 'code', 'link', 'voucher', 'label'],
    sheet: '印出二维码页',
    void: '作废遗失的卡',
    voidLead: '作废的卡扫描时会被拒绝。已领取的卡保留它的优惠券。',
    firstSerial: '从卡号',
    lastSerial: '到卡号',
    voidSubmit: (cards, count) => (cards === null ? '作废' : `作废 ${count(cards)} 张卡`),
    voiding: '作废中',
    keep: '取消',
    invalidSerial: '请输入卡号，例如 0021，第二个要在第一个之后。',
    outside: '这些卡不全在这批印刷里。',
    voidFailed: '无法作废卡片。请再试一次。',
    qrLabel: (serial) => `卡号 ${serial} 的二维码`,
    back: '返回活动',
  },
  campaignQr: {
    lead: '可以印出来，或在屏幕上显示。扫描后会打开带有这张优惠券的注册页面。',
    label: (link) => `${link} 的二维码`,
    copy: '复制链接',
    copied: '已复制',
    copyFailed: '无法复制。链接已选取，请自行复制。',
    print: '打印',
    saveSvg: '保存为 SVG',
    savePng: '保存为 PNG',
    back: '返回活动',
  },
  campaign: {
    crumb: '活动',
    summary: (kind, vouchers, dates, given, used, count, top) => [`${kind} · `, { figure: count(vouchers) }, ` 张优惠券 · ${dates}。`, { figure: `已发出 ${count(given)} 张` }, '，', { figure: `已使用 ${count(used)} 张` }, '。', ...(top === null ? [] : [`主要靠 ${top}。`])],
    dates: (from, until) => (until === null ? `${from} 起` : `${from} 至 ${until}`),
    exportCsv: '导出 CSV',
    addVoucher: '添加优惠券',
    given: '已发出',
    givenNote: (heldBack, opened) => `保留 ${heldBack} · 已打开 ${opened}`,
    used: '已使用',
    usedNote: (rate) => `占已发出的 ${rate}`,
    valueGiven: '发出面值',
    valueUsed: '使用面值',
    valueNote: '按面值',
    timeToUse: '使用前的时间',
    timeNote: (cancelled, expired, median) => `${median ? '中位数，最多' : '还没有人使用'} · 收回 ${cancelled} · 过期 ${expired}`,
    noneUsed: '无',
    minutes: (minutes) => `${minutes} 分钟`,
    hours: (hours) => `${hours} 小时`,
    within: (time) => time,
    beyond: '超过 30 天',
    range: (from, to) => `${from} 至 ${to}`,
    totalOf: (vouchers, count) => `总计 · ${count(vouchers)} 张优惠券`,
    days: (days) => `${days} 天`,
    split: '优惠券分布',
    splitGiven: '已发出',
    splitUsed: '已使用',
    splitEmpty: '暂时没有。',
    vouchersTitle: '每张优惠券',
    columns: { voucher: '优惠券', cards: '卡号', chance: '机会', given: '已发出', left: '剩余', used: '已使用', rate: '使用率', valueUsed: '使用面值' },
    noLimit: '不限',
    chance: (weight, total, percent) => `${weight} / ${total}，${percent}`,
    total: '总计',
    csvName: (name) => `${name} 成效`,
    byMedium: '来源方式',
    byMediumEmpty: '还没有通过带标记的链接领取的优惠券。',
    qr: 'QR',
    qrTitle: (voucher) => `${voucher} 的 QR`,
    qrGives: (vouchers) => (vouchers === 1 ? '扫描后打开注册页面，并领取这张优惠券。' : '扫描后打开注册页面，只领取这张优惠券，不含活动的其他优惠券。'),
    campaignQr: '活动 QR',
    campaignQrGives: (vouchers, randomOne) => (vouchers === 1 ? '扫描后打开注册页面，并领取活动的优惠券。' : randomOne ? `扫描后打开注册页面，按权重随机领取活动 ${String(vouchers)} 张优惠券中的一张。每张优惠券在它的那一行也有自己的 QR，扫描后领取那一张。` : `扫描后打开注册页面，领取活动全部 ${String(vouchers)} 张优惠券。每张优惠券在它的那一行也有自己的 QR。`),
  },
  mediums: { qr: 'QR 码', link: '链接', print: '印刷卡', station: '优惠券机' },
  catalogue: {
    title: '优惠券',
    summary: (offers, given, used, count) => ['目录里有 ', { figure: count(offers) }, ' 种优惠券，已发出 ', { figure: count(given) }, ' 张，已使用 ', { figure: count(used) }, ' 张。'],
    newVoucher: '新优惠券',
    loading: '载入中',
    loadFailed: '无法载入优惠券。',
    none: '还没有优惠券。新的优惠券可以放进活动，或有自己的二维码。',
    columns: { voucher: '优惠券', given: '已发出', used: '已使用', status: '状态' },
    active: '在目录中',
    archived: '已归档',
    revision: (revision) => `第 ${String(revision)} 版`,
    name: '名称',
    nameHint: '会员会在优惠券上看到，例如“免费 100 克”。',
    partOf: '属于一个活动',
    campaign: '活动',
    campaignPick: '选择活动',
    noCampaigns: '没有进行中的活动可以再加优惠券。',
    severalTake: '活动二维码、领券机、印刷卡和加入即送的活动可以送几种优惠券；推荐朋友或指定客群的活动只能送一种。',
    makeQr: '为它制作二维码',
    makeQrHint: '一个独立的活动：扫描二维码的人都可以领一次这张优惠券。',
    save: '保存优惠券',
    saving: '保存中',
    saved: (name) => `${name} 已加入目录。`,
    savedIn: (name, campaign) => `${name} 已加入目录，${campaign} 现在会送出它。`,
    qrTitle: (name) => `${name} 的二维码`,
    failed: '无法保存优惠券。请再试一次。',
    refused: (campaign, reason) => `优惠券已加入目录，但 ${campaign} 没有接受：${reason}`,
    invalidName: '请为优惠券命名。',
    invalidDays: '请输入整数天数，例如 30。',
    edit: (name) => `编辑 ${name}`,
    editTitle: '编辑优惠券',
    editLead: (revision) => `保存后会生成这张优惠券的第 ${String(revision + 1)} 版。已发出的优惠券保持第 ${String(revision)} 版和当时的条款：会员手上的优惠券不会改变。`,
    editTermsKept: '可用的日子、时段和分店保持不变。',
    termsCount: (lines) => (lines === 0 ? '没有条款' : `${String(lines)} 条条款`),
    editCampaigns: (names) => `在现在发放它的活动里改发新条款：${names}`,
    editCampaignsHint: (revision) => `不勾选的话，这些活动会继续发第 ${String(revision)} 版，直到重新设置。`,
    picture: '图片',
    pictureHint: 'PNG 或 JPEG，最大 2 MB，方形最好。这个优惠的每张优惠券都会在会员页面显示它，已发出的也一样。图片会立即保存，不会产生新版本。',
    picturePick: '添加图片',
    pictureChange: '更换图片',
    pictureRemove: '移除',
    pictureUploading: '上传中',
    pictureTooBig: '图片超过 2 MB，请选小一点的。',
    pictureWrongType: '请选 PNG 或 JPEG 图片。',
    pictureFailed: '图片没有保存成功，请再试一次。',
    editSave: '保存新版本',
    editSaved: (name, revision) => `${name} 现在是第 ${String(revision)} 版。`,
    editSavedCampaigns: (count) => `从现在起，${String(count)} 个活动发放新条款。`,
  },
  voucher: {
    off: '折扣',
    offUpTo: (cap) => `折扣，最高 ${cap}`,
    free: '免费',
    grams: (grams) => `${String(grams)} 克`,
    everyDay: '每天',
    anyTime: '随时可用',
    day: { mon: '周一', tue: '周二', wed: '周三', thu: '周四', fri: '周五', sat: '周六', sun: '周日' },
    from: (date) => `${date} 起`,
    until: (date) => `有效至 ${date}`,
    noEnd: '没有截止日期',
    goodFor: (days) => `${String(days)} 天内有效`,
    foodOnly: '只限食物，不包括饮料',
    minSpend: (amount) => `最低消费 ${amount}`,
    minGrams: (grams) => `最少点 ${String(grams)} 克`,
    conditions: '条款与条件',
    onePerBill: '每单限用一张',
    onlyAt: (outlets) => `只限 ${outlets.join('、')}`,
    brandOnly: (brands) => `只限 ${brands.join('、')} 的分店`,
    status: { usable: '可使用', redeemed: '已使用', revoked: '已收回', lapsed: '已过期未使用' },
  },
};

const ms: CrmStrings = {
  join: {
    brandSub: 'Ahli',
    title: (name) => (name === null ? 'Sertai sebagai ahli' : `Sertai ahli ${name}`),
    lead: 'Daftar dengan nombor telefon anda dan dapatkan baucar anda.',
    phoneLabel: 'Nombor telefon',
    phoneHint: (example) => (example === null ? 'Pilih negara anda, kemudian taip nombor telefon bimbit anda.' : `Pilih negara anda, kemudian taip nombor telefon bimbit anda, contohnya ${example}.`),
    phoneInvalid: (example) => (example === null ? 'Masukkan nombor telefon bimbit negara yang dipilih.' : `Masukkan nombor telefon bimbit, contohnya ${example}.`),
    sendCode: 'Hantar kod',
    sending: 'Menghantar kod',
    tooManyCodes: (minutes) => (minutes === null ? 'Terlalu banyak kod untuk nombor ini. Cuba lagi dalam beberapa minit.' : `Terlalu banyak kod untuk nombor ini. Cuba lagi dalam ${String(minutes)} minit.`),
    codeBudgetSpent: 'Kod pendaftaran dihentikan untuk hari ini. Sila tanya kakitangan, atau cuba lagi esok.',
    voucherGone: 'Baucar QR ini tidak diberikan lagi. Minta QR terkini daripada kakitangan.',
    codeTitle: 'Semak telefon anda',
    codeSentTo: (phone) => `Kami telah menghantar kod 6 digit melalui SMS ke ${phone}.`,
    changeNumber: 'Tukar nombor',
    codeLabel: (phone) => `Kod 6 digit yang kami hantar ke ${phone}`,
    codeInvalid: 'Masukkan kod 6 digit daripada SMS.',
    codeWrong: (triesLeft) => (triesLeft > 0 ? `Kod itu tidak betul. Tinggal ${String(triesLeft)} cubaan lagi.` : 'Kod itu tidak betul.'),
    resend: 'Hantar kod baharu',
    resendIn: (seconds) => `Hantar kod baharu dalam ${String(seconds)} s`,
    resent: 'Kod baharu telah dihantar. Kod lama tidak boleh digunakan lagi.',
    notDelivered: 'Kami tidak dapat menghantar kod. Sila cuba lagi.',
    codeExpired: 'Kod itu sudah tamat tempoh. Hantar kod baharu.',
    tooManyAttempts: 'Terlalu banyak kod salah. Hantar kod baharu.',
    noCodeSent: 'Kod belum sampai. Hantar kod baharu jika ia tidak sampai.',
    startAgain: 'Mula semula',
    challengeGone: 'Pendaftaran ini sudah selesai atau tamat masa. Sila mula semula.',
    noticeOutdated: 'Halaman ini telah dikemas kini. Muat semula untuk melihat notis privasi terkini.',
    reload: 'Muat semula halaman',
    detailsHeading: 'Tentang anda',
    nameLabel: 'Nama',
    nameRequired: 'Masukkan nama anda.',
    genderLabel: 'Jantina',
    genders: { female: 'Perempuan', male: 'Lelaki' },
    genderRequired: 'Pilih satu.',
    raceLabel: 'Bangsa',
    races: { malay: 'Melayu', chinese: 'Cina', indian: 'India', others: 'Lain-lain' },
    raceRequired: 'Pilih satu.',
    year: 'Tahun',
    birthdayLabel: 'Tarikh lahir',
    inviteLabel: 'Kod jemputan',
    inviteHint: 'Jika ada ahli yang memberi anda kod mereka.',
    inviteInvalid: 'Kod jemputan ialah 8 huruf dan digit, contohnya 7KQ2M9XA.',
    inviteUnknown: 'Tiada ahli yang mempunyai kod ini. Semak semula, atau biarkan kosong.',
    day: 'Hari',
    month: 'Bulan',
    months: ['Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun', 'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember'],
    monthsShort: ['Jan', 'Feb', 'Mac', 'Apr', 'Mei', 'Jun', 'Jul', 'Ogo', 'Sep', 'Okt', 'Nov', 'Dis'],
    birthdayIncomplete: 'Pilih hari, bulan dan tahun anda dilahirkan.',
    birthdayNoSuchDate: 'Bulan itu tiada hari tersebut.',
    controllerUnknown: 'perniagaan ini',
    noticeHeading: 'Notis privasi',
    noticeDraftBadge: (version, controller) => `Draf daripada ${controller}, belum muktamad. Versi ${version}`,
    notice: (controller) => [
      `${capitalised(controller)} menyimpan nombor telefon bimbit, nama, jantina, tarikh lahir (termasuk tahun) dan bangsa anda, bersama rekod lawatan dan baucar anda, untuk menguruskan keahlian anda, merancang tawaran ahli dan menghantar tawaran yang anda persetujui.`,
      'Data ini disimpan oleh syarikat yang mengendalikan sistem untuk kami, di pelayan di Singapura, dan tidak sekali-kali dijual.',
      `Anda boleh meminta ${controller} untuk melihat, membetulkan atau memadam data anda pada bila-bila masa.`,
    ],
    noticeFull: 'Baca notis privasi penuh',
    marketingHeading: 'Tawaran dan berita',
    marketingLead: (controller) => `Pilih cara ${controller} boleh menghantar tawaran kepada anda. Anda boleh menukarnya kemudian.`,
    marketingWhatsApp: 'Tawaran melalui WhatsApp',
    marketingSms: 'Tawaran melalui SMS',
    marketingAnswers: { yes: 'Ya', no: 'Tidak' },
    marketingRequired: 'Pilih ya atau tidak.',
    agreement: 'Dengan menyertai, anda mengesahkan bahawa anda telah membaca notis privasi.',
    submit: 'Sertai',
    submitting: 'Menyertai',
    erasedTitle: 'Nombor ini tidak boleh menyertai lagi',
    erasedBody: 'Nombor ini pernah menjadi ahli, dan butirannya telah dipadam. Baucar alu-aluan adalah satu bagi setiap nombor telefon, jadi nombor ini tidak boleh mendapatkannya lagi.',
    linkInvalidTitle: 'Pautan ini tidak sah',
    linkInvalidBody: 'Minta pautan baharu daripada cawangan.',
    linkInvalid: 'Pautan ini tidak sah. Minta pautan baharu daripada cawangan.',
  },
  privacy: {
    title: 'Notis privasi',
    lead: (controller) => `${capitalised(controller)} ([nama dan nombor pendaftaran syarikat]) memberikan notis ini kepada anda di bawah Akta Perlindungan Data Peribadi 2010 Malaysia. Notis ini menerangkan apa yang kami kumpul apabila anda menyertai sebagai ahli, sebabnya, dan apa yang boleh anda lakukan.`,
    sections: (controller) => [
      {
        heading: 'Apa yang kami kumpul',
        paragraphs: [
          'Semasa menyertai: nombor telefon bimbit, nama, jantina, tarikh lahir (termasuk tahun) dan bangsa anda. Kami mengesahkan nombor anda dengan kod sekali guna yang dihantar kepadanya melalui SMS.',
          'Kami juga merekodkan bahasa halaman tempat anda menyertai, pautan atau kod QR yang anda gunakan untuk menyertai, kod jemputan yang anda taip, dan jawapan anda tentang tawaran melalui WhatsApp dan SMS.',
          'Semasa anda menggunakan keahlian: baucar yang anda terima dan gunakan, setiap lawatan apabila kakitangan mencari anda di kaunter, bersama cawangan dan masanya, dan rakan yang menyertai melalui pautan jemputan anda.',
          'Kami tidak meminta e-mel, nombor kad pengenalan, alamat atau agama anda, dan kami tidak mengumpul data peribadi sensitif, seperti kesihatan atau kepercayaan politik atau agama anda.',
        ],
      },
      {
        heading: 'Mengapa kami menggunakannya',
        paragraphs: [
          'Untuk menguruskan keahlian anda: untuk log masuk, memberi baucar anda, menyemaknya di kaunter, dan memastikan setiap nombor telefon bimbit hanya mendapat baucar alu-aluan sekali.',
          'Untuk memahami ahli kami secara keseluruhan, mengikut umur, jantina dan bangsa, supaya kami dapat merancang menu, cawangan dan tawaran kami.',
          'Untuk memilih tawaran yang sesuai dengan anda, seperti hadiah hari jadi atau tawaran untuk pelanggan tetap, berdasarkan lawatan dan baucar anda.',
          'Untuk menghantar tawaran melalui WhatsApp atau SMS, hanya melalui saluran yang anda setujui, dalam bahasa yang anda gunakan semasa menyertai.',
          'Untuk menyimpan rekod yang dikehendaki oleh undang-undang, dan untuk mengesan dan menghalang penyalahgunaan baucar.',
        ],
      },
      {
        heading: 'Dengan siapa kami berkongsi',
        paragraphs: [
          'Kami tidak menjual data anda. Kami hanya berkongsinya dengan syarikat yang mengendalikan perkhidmatan untuk kami, di bawah kontrak, dan setiap satu hanya mendapat apa yang diperlukannya: pembekal sistem keahlian kami; Google Cloud, yang menempatkannya di Singapura, jadi data anda disimpan di luar Malaysia; pembekal SMS yang menghantar kod sekali guna dan tawaran SMS anda; dan WhatsApp (Meta), hanya jika anda bersetuju menerima tawaran melalui WhatsApp.',
          'Kami memberikan data anda kepada pihak berkuasa hanya apabila dikehendaki oleh undang-undang.',
        ],
      },
      {
        heading: 'Berapa lama kami menyimpannya',
        paragraphs: [
          'Kami menyimpan butiran anda selagi anda menjadi ahli. Apabila anda meminta kami memadamnya, kami memadam nombor telefon bimbit, nama, jantina, tarikh lahir dan bangsa anda. Kami hanya menyimpan kod sehala yang dibuat daripada nombor anda, yang tidak boleh ditukar semula kepada nombor itu, supaya nombor yang sama tidak boleh mendapat baucar alu-aluan sekali lagi.',
          'Sejarah lawatan dan baucar anda kekal, tanpa dikaitkan dengan sebarang nama atau nombor, supaya kami dapat mengira prestasi tawaran kami. Log setiap permintaan kepada sistem kami tidak mengandungi butiran peribadi dan dipadam selepas 3 tahun.',
        ],
      },
      {
        heading: 'Pilihan dan hak anda',
        paragraphs: [
          'Setiap soalan dalam borang mesti dijawab untuk menyertai, kecuali kod jemputan. Jika anda membiarkan satu kosong, anda tidak boleh menyertai.',
          'Tawaran melalui WhatsApp dan SMS adalah pilihan anda, setiap satu secara berasingan, dan menjawab tidak tidak menghalang anda daripada menyertai. Untuk menghentikan mana-mana satu pada bila-bila masa, beritahu kakitangan di mana-mana cawangan atau tulis kepada kami.',
          `Anda boleh meminta ${controller} untuk melihat data anda, membetulkannya, memberi anda salinannya, atau memadamnya, dan anda boleh menarik balik persetujuan anda pada bila-bila masa. Tulis kepada kami di alamat di bawah; kami menjawab dalam masa 21 hari. Menarik balik persetujuan untuk menyimpan data anda menamatkan keahlian anda.`,
          'Anda juga boleh membuat aduan kepada Jabatan Perlindungan Data Peribadi (JPDP).',
        ],
      },
      {
        heading: 'Cara menghubungi kami',
        paragraphs: [
          `${capitalised(controller)}, [nama dan nombor pendaftaran syarikat], [alamat pos].`,
          'Pegawai perlindungan data: [nama], [e-mel], [nombor telefon].',
          'Kami mungkin mengemas kini notis ini; versi yang anda persetujui direkodkan semasa anda menyertai. Notis ini dalam bahasa Inggeris, Melayu dan Cina; jika terdapat perbezaan, versi bahasa Inggeris diguna pakai. Kemas kini terakhir 29 Oktober 2026.',
        ],
      },
    ],
  },
  members: {
    title: 'Ahli',
    codesPaused: (budget) => `Had ${budget} kod log masuk hari ini telah habis digunakan. Kod pendaftaran baharu dihentikan sehingga esok, atau sehingga anda menaikkan had.`,
    codesNotDelivered: (failed, tried) => `${failed} daripada ${tried} kod log masuk hari ini tidak dapat dihantar. Pelanggan boleh meminta kod baharu dengan segera. Jika jumlah ini terus meningkat, perkhidmatan SMS mungkin terganggu.`,
    undelivered: {
      open: 'Lihat pendaftaran mana',
      title: 'Kod tidak sampai hari ini',
      lead: 'Setiap pendaftaran yang kodnya tidak dapat dihantar hari ini, dengan percubaan gagal terakhirnya. Nombor telefon disembunyikan sebahagian.',
      columns: { phone: 'Telefon', time: 'Masa', reason: 'Sebab', source: 'Sumber', since: 'Selepas itu' },
      reasons: {
        'gateway down': 'Perkhidmatan SMS terganggu',
        'number rejected': 'Nombor ditolak oleh perkhidmatan SMS',
        'channel not configured': 'Penghantaran SMS belum disediakan',
        'timed out': 'Perkhidmatan SMS tidak menjawab',
        'no phone on record': 'Tiada nombor telefon dalam rekod',
      },
      since: { joined: 'Telah menyertai', gotCode: 'Menerima kod kemudian', noCode: 'Tiada kod sejak itu' },
      noPhone: 'Tidak lagi disimpan',
      more: (count) => `dan ${count} lagi`,
    },
    refresh: 'Muat semula',
    refreshing: 'Memuat semula',
    totalMembers: 'Jumlah ahli',
    totalErased: (count) => `(${count} dipadam)`,
    bySource: 'Mengikut sumber',
    byDate: 'Mengikut tarikh sertai',
    funnel: 'Pendaftaran',
    funnelStarted: 'Minta kod',
    funnelSent: 'Kod dihantar',
    funnelJoined: 'Menyertai',
    funnelNotDelivered: 'Kod tidak sampai',
    funnelWrongCodes: 'Kod salah ditaip',
    noMembers: 'Belum ada ahli. Mereka akan muncul di sini sebaik sahaja seseorang menyertai.',
    noData: 'Belum ada apa-apa',
    columns: { member: 'Ahli', source: 'Sumber', whatsApp: 'WhatsApp', sms: 'SMS', visits: 'Lawatan', lastVisit: 'Lawatan terakhir', since: 'Sejak lawatan terakhir', mostVisited: 'Paling kerap dilawati', race: 'Bangsa', gender: 'Jantina', age: 'Umur' },
    never: 'Belum pernah',
    noValue: '-',
    sinceDays: (days, count) => (days <= 0 ? 'Hari ini' : `${count(days)} hari`),
    unknownOutlet: 'Cawangan tidak diketahui',
    revealing: 'Memuatkan',
    revealFailed: 'Tidak dapat dimuatkan',
    summary: (total, week, count) => [{ figure: count(total) }, ' ahli, ', { figure: count(week) }, ' menyertai dalam 7 hari lepas.'],
    kpiWeek: 'Menyertai, 7 hari lepas',
    kpiWeekNote: (from) => `Sejak ${from}`,
    kpiConversion: 'Kadar pendaftaran',
    kpiConversionNote: (joined, asked) => `${joined} menyertai daripada ${asked} yang meminta kod`,
    kpiConsent: 'Setuju terima tawaran',
    kpiConsentNote: (shown) => `WhatsApp atau SMS, daripada ${shown} dipaparkan`,
    search: 'Cari nama, telefon, nombor ahli atau sumber',
    numbered: (number) => `No. ${number}`,
    sourceFilter: 'Sumber',
    allSources: 'Semua sumber',
    filters: {
      label: 'Penapis',
      clear: 'Kosongkan penapis',
      done: (shown) => `Papar ${shown}`,
      medium: 'Datang melalui',
      allMediums: 'Apa-apa cara',
      noMedium: 'Tiada tag',
      country: 'Negara',
      allCountries: 'Semua negara',
      gender: 'Jantina',
      allGenders: 'Semua jantina',
      age: 'Umur',
      allAges: 'Semua umur',
      band: (from, to) => (from === 0 && to !== null ? `Bawah ${String(to + 1)}` : to === null ? `${String(from)} ke atas` : `${String(from)} hingga ${String(to)}`),
      birthMonth: 'Bulan lahir',
      allMonths: 'Apa-apa bulan',
      joinedFrom: 'Menyertai dari',
      joinedUntil: 'Menyertai hingga',
      notGiven: 'Tidak diberi',
      lastVisit: 'Lawatan terakhir',
      lastOutlet: 'Cawangan terakhir',
      spend: 'Jumlah perbelanjaan',
      soonLead: 'Memerlukan bil kiosk',
    },
    export: {
      label: 'Eksport ke Excel',
      busy: 'Mengeksport',
      failed: 'Tidak dapat mengeksport ahli. Cuba lagi.',
      fileName: (date) => `Ahli ${date}`,
      columns: { memberNumber: 'No. ahli', name: 'Nama', phone: 'Telefon', gender: 'Jantina', yearOfBirth: 'Tahun lahir', birthMonth: 'Bulan lahir', country: 'Negara', source: 'Sumber', medium: 'Datang melalui', utmSource: 'Sumber UTM', utmCampaign: 'Kempen UTM', joined: 'Menyertai', whatsApp: 'Tawaran melalui WhatsApp', sms: 'Tawaran melalui SMS', vouchers: 'Baucar', used: 'Digunakan', visits: 'Lawatan', lastVisit: 'Lawatan terakhir', lastOutlet: 'Cawangan terakhir', daysSince: 'Hari sejak lawatan terakhir', mostVisited: 'Cawangan paling kerap dilawati', race: 'Bangsa' },
    },
    showing: (shown, loaded) => `${shown} daripada ${loaded} dipaparkan`,
    noMatch: 'Tiada ahli yang dipaparkan sepadan. Tukar carian, atau papar lebih banyak ahli.',
    open: (name) => `Buka ${name}`,
    erasedOn: (date) => `Dipadam pada ${date}`,
    sheet: {
      profile: 'Profil',
      memberNumber: 'No. ahli',
      phone: 'Telefon',
      gender: 'Jantina',
      yearOfBirth: 'Tahun lahir',
      race: 'Bangsa',
      country: 'Negara',
      medium: 'Datang melalui',
      campaign: 'Kempen UTM',
      name: 'Nama',
      source: 'Sumber',
      joined: 'Menyertai',
      offers: 'Tawaran melalui',
      yes: 'Ya',
      no: 'Tidak',
      vouchers: 'Baucar',
      vouchersCount: (given, used) => `${String(given)} dimiliki · ${String(used)} digunakan`,
      vouchersLoading: 'Memuatkan baucar',
      vouchersFailed: 'Tidak dapat memuatkan baucar.',
      noVouchers: 'Belum ada baucar.',
      points: 'Mata',
      pointsLead: 'Mata yang diperoleh setiap lawatan, dan baki untuk dibelanjakan.',
      tier: 'Tahap',
      tierLead: 'Tahap ahli dan apa yang dibukanya.',
      visits: 'Lawatan',
      visitsCount: (visits, count) => `${count(visits)} lawatan`,
      lastVisit: 'Lawatan terakhir',
      mostVisited: 'Paling kerap dilawati',
      noVisits: 'Belum ada lawatan. Satu lawatan ialah QR ahli diimbas di stesen juruwang.',
    },
    noName: 'Tidak diberi',
    erased: 'Dipadam',
    showMore: 'Papar lagi',
    loading: 'Memuatkan',
    loadFailed: 'Tidak dapat memuatkan ahli.',
    retry: 'Cuba lagi',
    shareTitle: 'Pautan pendaftaran',
    shareLead: 'Buat pautan untuk poster, kod QR atau kempen. Setiap orang yang menyertai melaluinya disenaraikan dengan tag sumbernya.',
    shareTagLabel: 'Tag sumber',
    shareTagHint: 'Tempat pautan diletakkan, contohnya hoarding atau poster-a.',
    shareTagInvalid: 'Guna huruf kecil, digit dan sengkang, sehingga 40, contohnya poster-a.',
    shareEmpty: 'Taip tag sumber untuk mendapatkan pautannya.',
    shareCopy: 'Salin pautan',
    shareCopied: 'Disalin',
    shareCopyFailed: 'Tidak dapat menyalin. Pautan sudah dipilih: salin sendiri.',
    shareQrLabel: (link) => `Kod QR untuk ${link}`,
    shareQrHint: 'Imbas dengan kamera telefon untuk membuka pautan.',
  },
  channels: {
    whatsApp: 'WhatsApp',
    sms: 'SMS',
  },
  member: {
    title: 'Keahlian anda',
    welcomeJoined: (name) => (name === null ? 'Selamat datang, anda kini ahli' : `Selamat datang ke ahli ${name}`),
    welcomeBack: 'Selamat kembali',
    qrTitle: 'QR ahli anda',
    qrLabel: 'Kod QR ahli anda',
    qrHint: 'Imbas di kaunter untuk baucar',
    vouchersTitle: 'Baucar saya',
    loading: 'Memuatkan baucar anda',
    loadFailed: 'Tidak dapat memuatkan baucar anda.',
    noVouchers: 'Tiada baucar buat masa ini.',
    endedTitle: (count) => `Digunakan dan tamat (${String(count)})`,
    entered: 'Baucar daripada QR yang anda imbas kini milik anda.',
    cardLimit: 'Anda sudah menuntut jumlah maksimum kad kempen ini bagi seorang ahli. Kad ini kekal tidak digunakan.',
    enterRefused: {
      'not a member': 'Telefon ini tidak log masuk ke mana-mana keahlian.',
      'member erased': 'Keahlian ini telah dipadam.',
      'offer not in campaign': 'Baucar QR ini tidak diberikan lagi.',
      'already entered': 'Anda sudah mempunyai baucar daripada QR ini.',
      'campaign not open': 'Baucar daripada QR ini tidak tersedia buat masa ini.',
      'campaign ended': 'Baucar daripada QR ini sudah tamat.',
      'campaign full': 'Semua baucar daripada QR ini sudah habis diberikan.',
      'over budget': 'Semua baucar daripada QR ini sudah habis diberikan.',
      'draw used': 'QR ini sudah digunakan.',
      'card used': 'Kad ini sudah digunakan.',
      'card void': 'Kad ini tidak sah lagi.',
    },
    drawRefused: {
      'not a member': 'Telefon ini tidak log masuk ke mana-mana keahlian.',
      'member erased': 'Keahlian ini telah dipadam.',
      'already entered': 'Anda sudah mendapat ganjaran daripada cabutan ini. Satu ganjaran setiap orang.',
      'draw used': 'QR ini sudah digunakan. Tekan butang sekali lagi untuk QR baharu.',
      'card used': 'Kad ini sudah digunakan.',
      'card void': 'Kad ini tidak sah lagi.',
      'campaign not open': 'Cabutan tidak berjalan buat masa ini.',
      'campaign ended': 'Cabutan ini sudah tamat.',
      'campaign full': 'Semua ganjaran cabutan ini sudah habis diberikan.',
      'over budget': 'Semua ganjaran cabutan ini sudah habis diberikan.',
    },
    withheld: {
      'campaign not open': 'Baucar daripada QR yang anda imbas tidak tersedia buat masa ini.',
      'campaign ended': 'Baucar daripada QR yang anda imbas sudah tamat.',
      'campaign full': 'Semua baucar daripada QR yang anda imbas sudah habis diberikan.',
      'over budget': 'Semua baucar daripada QR yang anda imbas sudah habis diberikan.',
      'already entered': 'Anda sudah mendapat ganjaran daripada cabutan ini. Satu ganjaran setiap orang.',
      'draw used': 'QR ini sudah digunakan, jadi tiada ganjaran diberikan. Tekan butang sekali lagi untuk QR baharu.',
      'card used': 'Kad ini sudah digunakan, jadi tiada ganjaran diberikan.',
      'card void': 'Kad ini tidak sah lagi, jadi tiada ganjaran diberikan.',
    },
    inviteTitle: 'Jemput rakan',
    inviteCodeLabel: 'Kod jemputan anda',
    inviteLead: 'Kongsi pautan anda, atau beritahu rakan kod anda untuk ditaip semasa mereka menyertai. Ganjaran jemputan anda akan muncul dalam baucar anda.',
    inviteCount: (joined, rewards) => `${String(joined)} rakan menyertai · ${String(rewards)} ganjaran`,
    inviteWhatsApp: 'Kongsi di WhatsApp',
    inviteCopy: 'Salin pautan saya',
    inviteCopyCode: 'Salin kod saya',
    inviteCodeCopied: 'Kod disalin',
    inviteMessage: (link, code, name) => `${name === null ? 'Sertai sebagai ahli' : `Sertai ahli ${name}`} dengan pautan saya dan dapatkan baucar alu-aluan anda: ${link}\n\nAtau taip kod jemputan saya ${code} semasa anda menyertai.`,
    notYou: 'Bukan anda?',
    signOut: 'Log keluar dari telefon ini',
    replaced: 'QR ahli anda telah berpindah ke telefon lain. Log masuk semula untuk menggunakannya di sini.',
    signInTitle: 'Keahlian anda',
    signInLead: 'Log masuk dengan nombor telefon anda untuk melihat QR ahli dan baucar anda.',
    signIn: 'Log masuk',
    signingIn: 'Sedang log masuk',
    notMemberTitle: 'Nombor ini belum menjadi ahli',
    notMemberBody: 'Sertai dengan nombor ini untuk mendapatkan baucar anda.',
    joinNow: 'Sertai sekarang',
    standingTitle: 'Tahap dan pulangan tunai saya',
    tierLabel: 'Tahap',
    tierNote: (earned, from, next) => `${earned} / ${from} ke ${next}`,
    tierTo: (next) => ['Ke ', { figure: next }],
    tierCount: (earned, from) => [{ figure: earned }, ` / ${from} mata`],
    greeting: { morning: 'Selamat pagi,', afternoon: 'Selamat petang,', evening: 'Selamat malam,' },
    inviteHeadline: 'Makan sedap, kongsi dengan kawan',
    inviteBlurb: (name) => `Jemput rakan sertai ${name ?? 'kami'}. Anda berdua dapat ganjaran.`,
    tierTop: 'Tahap tertinggi',
    cashbackLabel: 'Pulangan tunai',
    cashbackPoints: (points, format) => `${format(points)} mata`,
    cashbackInfoLabel: 'Cara pulangan tunai berfungsi',
    cashbackInfo: (spent, points, format) => `Belanja ${spent} = ${format(points)} mata`,
    memberNumber: 'No. ahli',
    cardRow: 'Kad keahlian saya',
    cardRowSub: 'QR ahli anda, lebih besar untuk kaunter',
    cardLarge: 'Kod QR ahli anda, saiz besar',
    vouchersRowSub: (usable) => `${String(usable)} boleh digunakan`,
    storeRow: 'Kedai ganjaran',
    storeRowSub: 'Tebus ganjaran dengan mata',
    qrShow: 'Tunjukkan QR ahli saya',
    ready: (count, format) => `${format(count)} sedia`,
    fromReferral: 'Terima kasih kerana menjemput rakan',
    fromReferred: 'Kerana menyertai dengan jemputan rakan',
    more: 'Lagi untuk ahli',
  },
  lookup: {
    title: 'Cari ahli',
    phoneLabel: 'Telefon atau nombor ahli',
    phoneHint: ({ example, abroadExample }) => `Telefon seperti ${example} atau ${abroadExample}, atau nombor ahli seperti 100023`,
    phoneInvalid: ({ example, abroadExample }) => `Masukkan nombor telefon, contohnya ${example} atau ${abroadExample}, atau nombor ahli, contohnya 100023.`,
    find: 'Cari',
    finding: 'Mencari',
    notFound: 'Tiada ahli dengan nombor ini.',
    erased: 'Ahli ini telah dipadam.',
    joined: (date, source) => `Menyertai ${date} melalui ${source}`,
    memberNumber: (number) => `No. ahli ${number}`,
    noName: 'Tiada nama diberi',
    vouchers: 'Baucar',
    noVouchers: 'Belum ada baucar.',
    clear: 'Kosongkan',
    failed: 'Tidak dapat mencari ahli. Cuba lagi.',
  },
  redeem: {
    title: 'Tebus',
    outletLabel: 'Cawangan',
    outletPick: 'Pilih cawangan',
    outletMissing: 'Pilih cawangan tempat peranti ini berada.',
    noOutlets: 'Kumpulan ini belum mempunyai cawangan. Pengurus perlu menambahnya dahulu.',
    setupTitle: 'Sediakan stesen juruwang ini',
    setupLead: 'Pilih cawangan tempat peranti ini berada, sekali sahaja. Selepas itu kakitangan hanya mengimbas dan menebus di sini, dan setiap penebusan merekodkan cawangan ini.',
    stationName: 'Nama stesen',
    stationNameHint: 'Contohnya Juruwang depan.',
    stationNameMissing: 'Namakan stesen ini, contohnya Juruwang depan.',
    setupSubmit: 'Guna peranti ini di sini',
    settingUp: 'Sedang menyediakan',
    setupCancel: 'Batal',
    setupFailed: 'Tidak dapat menyediakan peranti ini. Cuba lagi.',
    stationAt: (outlet, station) => `${outlet} · ${station}`,
    change: 'Tukar',
    stationGone: 'Peranti ini bukan lagi stesen juruwang. Sediakannya semula.',
    scanTitle: 'Imbas QR ahli',
    scanLead: 'Ahli membuka halaman ahli mereka dan menunjukkan QR.',
    startCamera: 'Imbas dengan kamera',
    stopCamera: 'Hentikan kamera',
    cameraUnavailable: 'Kamera tidak tersedia. Guna pengimbas, atau benarkan kamera untuk halaman ini.',
    passLabel: 'QR ahli',
    passHint: 'Pengimbas QR akan menaipnya di sini.',
    find: 'Cari ahli',
    finding: 'Mencari ahli',
    unknownPass: 'Ini bukan QR ahli, atau ia telah berpindah ke telefon lain. Minta ahli membuka halaman ahli mereka sekali lagi.',
    memberTitle: (who) => `Ahli: ${who}`,
    noName: 'tiada nama diberi',
    noVouchers: 'Ahli ini tiada baucar untuk digunakan.',
    choose: 'Baucar untuk digunakan',
    redeem: (foodOnly) => (foodOnly === null ? 'Tebus' : foodOnly ? 'Tebus · makanan sahaja, satu setiap bil' : 'Tebus · satu setiap bil'),
    redeeming: 'Menebus',
    redeemed: 'Ditebus',
    redeemedAt: (outlet, time) => `Di ${outlet}, ${time}`,
    nextCustomer: 'Pelanggan seterusnya',
    askManager: 'Peranti ini belum menjadi stesen juruwang. Pengurus perlu log masuk di sini sekali untuk menyediakannya bagi cawangannya.',
    loadFailed: 'Tidak dapat memuatkan cawangan.',
    refused: {
      'scan the member QR again': 'Imbas QR ahli sekali lagi: imbasan itu sudah lebih 15 minit, atau dibuat di stesen lain.',
      'one voucher per visit': 'Lawatan ini sudah menggunakan baucar. Satu baucar setiap lawatan.',
      "not this member's voucher": 'Baucar ini milik ahli lain.',
      'already redeemed': 'Baucar ini sudah digunakan.',
      revoked: 'Baucar ini telah ditarik balik.',
      'not valid yet': 'Baucar ini belum boleh digunakan.',
      expired: 'Baucar ini sudah tamat.',
      'outside its days and hours': 'Baucar ini tidak boleh digunakan sekarang. Lihat hari dan waktunya.',
      'another outlet': 'Baucar ini untuk cawangan lain. Lihat di mana ia boleh digunakan.',
      'another brand': 'Baucar ini untuk cawangan jenama lain.',
    },
  },
  campaigns: {
    title: 'Kempen',
    summary: (open, given, used, count) => [{ figure: count(open) }, ' kempen dibuka, ', { figure: count(given) }, ' baucar diberikan, ', { figure: count(used) }, ' digunakan.'],
    details: 'Butiran',
    live: (count, format) => `${format(count)} aktif`,
    newCampaign: 'Kempen baharu',
    loading: 'Memuatkan',
    loadFailed: 'Tidak dapat memuatkan kempen.',
    retry: 'Cuba lagi',
    none: 'Belum ada kempen.',
    noOnJoin: 'Ahli baharu tidak mendapat baucar: tiada kempen dibuka yang memberi baucar semasa menyertai.',
    managersOnly: 'Kempen dan keputusannya hanya untuk pengurus.',
    makeOnJoin: 'Buat baucar penyertaan',
    open: 'Dibuka',
    ended: 'Tamat',
    starts: (date) => `Bermula ${date}`,
    kind: { 'on join': 'Semasa menyertai', 'by link': 'QR acara', 'by draw': 'Cabutan stesen baucar', 'by card': 'Kad bercetak', referral: 'Jemput rakan', 'to audience': 'Kepada kumpulan sasaran' },
    gives: (vouchers, rule) => (vouchers.length === 1 ? (vouchers[0] ?? '') : rule === 'random one' ? `satu secara rawak daripada ${vouchers.join(', ')}` : vouchers.join(' + ')),
    givesPerCard: (vouchers) => (vouchers.length === 1 ? (vouchers[0] ?? '') : `satu bagi setiap kad: ${vouchers.join(', ')}`),
    drawn: (pressed, claimed, count) => `${count(pressed)} tekanan di stesen, ${count(claimed)} dituntut.`,
    station: 'Stesen baucar',
    taken: 'diambil',
    and: ', ',
    used: (rate) => `digunakan, ${rate} daripada yang diambil.`,
    valueUsed: 'nilai muka digunakan.',
    endedFold: (ended, count) => `${count(ended)} kempen tamat`,
    given: (given, count) => `Diberikan: ${given.map((one) => `${count(one.count)} × ${one.what}`).join(', ')}`,
    usedSplit: (used, count) => `Digunakan: ${used.map((one) => `${count(one.count)} × ${one.what}`).join(', ')}`,
    turnedAway: (count) => `${String(count)} ditolak: penuh, melebihi bajet atau ditutup`,
    cardsTurnedAway: (count) => `${String(count)} ditolak kerana kad sudah digunakan atau tidak sah`,
    lift: (treated, holdout) => `Datang semula: ${treated} dengan baucar, ${holdout} tanpa baucar`,
    showQr: 'QR dan pautan',
    cards: 'Kad dan keputusan',
    end: 'Tamatkan',
    endConfirm: 'Tamatkan sekarang',
    ending: 'Menamatkan',
    keep: 'Kekalkan dibuka',
    endFailed: 'Tidak dapat menamatkan kempen. Cuba lagi.',
  },
  eventQr: {
    title: 'Kempen baharu',
    lead: {
      'by link': 'Setiap orang yang mengimbas QR ini akan menyertai atau log masuk, dan mendapat baucarnya sekali.',
      'by draw': 'Stesen baucar di cawangan menunjukkan QR baharu setiap kali butang ditekan: sesiapa yang mengimbasnya akan menyertai atau log masuk dan mendapat apa yang diberi oleh cabutan, satu ganjaran setiap orang.',
      'by card': 'Cetak kad untuk diedarkan dengan tangan, secara rawak jika anda mahu. Setiap kad mempunyai kod QR sendiri yang boleh digunakan sekali: sesiapa yang mengimbasnya akan menyertai atau log masuk dan mendapat baucar julat kad itu.',
      'on join': 'Setiap orang yang menyertai mendapat baucarnya, kecuali jika mereka menyertai melalui QR kempen lain.',
      referral: 'Ahli berkongsi pautan atau kod mereka. Rakan yang menyertai dengannya dikaitkan dengan mereka, dan jemputan itu memberi baucar ini pada masa dan kepada orang yang anda pilih.',
    },
    name: 'Nama',
    nameHint: 'Ahli melihatnya pada baucar, contohnya Minggu pembukaan di TRX.',
    gives: 'Cara ahli mendapatkannya',
    givesBy: { 'by link': 'Imbas QR acara', 'by draw': 'Tekan di stesen baucar', 'by card': 'Kad bercetak', 'on join': 'Setiap orang yang menyertai', referral: 'Jemput rakan' },
    rewardN: (n) => `Baucar ${String(n)}`,
    addReward: 'Tambah baucar lain',
    remove: 'Buang',
    removeReward: (title) => `Buang ${title}`,
    rule: 'Setiap ahli mendapat',
    rules: { all: 'Setiap baucar', 'random one': 'Satu secara rawak, mengikut pemberat' },
    ruleHint: 'Setiap baucar: setiap orang mendapat semuanya. Satu secara rawak: setiap imbasan memberi satu, dan baucar dengan pemberat 3 keluar tiga kali lebih kerap daripada baucar dengan pemberat 1. Untuk mengedarkan kad secara rawak dengan tangan, pilih Kad bercetak di atas.',
    weight: 'Pemberat',
    weightHint: (chance) => (chance === null ? 'Peluangnya ialah pemberatnya daripada jumlah semua pemberat.' : `Peluangnya: ${chance}.`),
    quantity: 'Berapa banyak untuk dikeluarkan',
    quantityHint: 'Apabila jumlah ini sudah diberikan, kempen berhenti memberi baucar ini. Biarkan kosong untuk tiada had.',
    benefit: 'Baucar',
    benefitKind: 'Apa yang diberi',
    benefits: (currency) => ({ 'amount off': `Diskaun ${currency}`, 'percent off': 'Diskaun %', 'free item': 'Item percuma', 'free grams': 'Gram percuma' }),
    amount: (currency) => `Jumlah diskaun (${currency})`,
    percent: 'Peratus diskaun',
    percentCap: (currency) => `Sehingga (${currency})`,
    item: 'Item yang diberi percuma',
    grams: 'Gram yang diberi percuma',
    days: 'Hari boleh digunakan',
    validDays: 'Sah selama (hari)',
    validDaysHint: 'Dikira dari hari ahli mengambilnya, atau dari hari pertamanya jika itu lebih lewat.',
    foodOnly: 'Makanan sahaja, bukan minuman',
    more: 'Lagi terma',
    hours: 'Waktu boleh digunakan',
    hoursFrom: 'Boleh digunakan dari',
    hoursUntil: 'Boleh digunakan hingga',
    minSpend: (currency) => `Perbelanjaan minimum (${currency})`,
    minGrams: 'Pesanan minimum (gram)',
    startsOn: 'Hari pertama boleh digunakan',
    startsOnHint: 'Ahli boleh memegangnya sebelum itu, contohnya dari hoarding hingga pembukaan.',
    outlets: 'Cawangan yang boleh digunakan',
    outletsHint: 'Biarkan semua tidak ditanda untuk semua cawangan.',
    brands: 'Jenama berkenaan',
    brandsHint: 'Digunakan di cawangan jenama itu, dengan termanya. Biarkan semua tidak ditanda untuk semua jenama.',
    giveFrom: 'Beri dari',
    giveUntil: 'Beri hingga',
    cap: 'Jumlah maksimum baucar diberi',
    capHint: 'Imbasan selepas itu akan dimaklumkan bahawa ia sudah penuh.',
    budget: (currency) => `Bajet (${currency})`,
    budgetHint: 'Nilai muka maksimum yang diberi, dikira semasa baucar diambil.',
    source: 'Nama pautan',
    sourceHint: 'Ahli yang menyertai melalui QR ini disenaraikan dengannya.',
    alsoOn: 'Juga untuk orang yang menyertai melalui',
    alsoOnHint: 'Setiap orang yang mendaftar melalui poster atau halaman mendapatnya. Nyahtanda cara yang anda mahu beri baucarnya sendiri sahaja.',
    routes: { 'event QR': 'QR acara', draw: 'Cabutan stesen baucar', 'printed card': 'Kad bercetak', referral: 'Jemputan rakan' },
    referralWhen: 'Diberi apabila',
    referralWhenBy: { 'on sign-up': 'Rakan menyertai', 'on first redemption': 'Rakan menggunakan baucar pertama' },
    referralTo: 'Siapa yang mendapat',
    referralToBy: { both: 'Kedua-duanya', inviter: 'Ahli yang menjemput', invitee: 'Rakan' },
    perInviter: 'Ganjaran maksimum setiap ahli',
    perInviterHint: 'Jemputan selepas had ini masih mengaitkan rakan, tetapi ahli tidak mendapat apa-apa lagi.',
    cardCount: 'Kad',
    cardLabel: 'Label',
    cardLabelHint: 'Contohnya mesin atau perisa.',
    serials: 'Nombor kad',
    serialsPreview: (total, first, last, count) => `${count(total)} kad, ${first} hingga ${last}`,
    prefix: 'Bermula dengan',
    prefixHint: 'Contohnya KM-TRX-',
    digits: 'Digit',
    firstSerial: 'Nombor pertama',
    perMember: 'Kad yang boleh dituntut seorang ahli',
    perMemberHint: 'Biarkan kosong untuk tiada had.',
    optional: 'pilihan',
    submit: { 'by link': 'Buat QR', 'by draw': 'Buat cabutan', 'by card': 'Buat kad', 'on join': 'Mula memberi', referral: 'Mulakan jemputan' },
    submitting: { 'by link': 'Membuat QR', 'by draw': 'Membuat cabutan', 'by card': 'Membuat kad', 'on join': 'Memulakan', referral: 'Memulakan jemputan' },
    cancel: 'Batal',
    invalid: {
      name: 'Beri kempen ini nama.',
      amount: (currency) => `Masukkan jumlah diskaun dalam ${currency}, contohnya 5 atau 2.50.`,
      percent: 'Masukkan peratus dari 1 hingga 100.',
      item: 'Namakan item percuma.',
      grams: 'Masukkan gram percuma, contohnya 100.',
      days: 'Pilih sekurang-kurangnya satu hari.',
      validDays: 'Masukkan bilangan hari, contohnya 7.',
      hours: 'Beri kedua-dua waktu, yang kedua selepas yang pertama.',
      whole: 'Masukkan nombor lebih daripada sifar.',
      until: 'Hari terakhir memberi adalah sebelum hari pertama.',
      source: 'Guna huruf kecil, digit dan sengkang, sehingga 40.',
      cards: 'Masukkan bilangan kad, contohnya 250.',
      perInviter: 'Masukkan nombor dari 1 hingga 100.',
      tooMany: (max, count) => `Satu cetakan membuat paling banyak ${count(max)} kad.`,
      prefix: 'Guna huruf besar, digit dan sengkang, contohnya KM-TRX-.',
      digits: 'Masukkan 1 hingga 8 digit.',
      moreDigits: 'Nombor kad terakhir memerlukan lebih banyak digit.',
      first: 'Masukkan nombor kad pertama, contohnya 1.',
      weight: (max) => `Masukkan nombor bulat dari 1 hingga ${String(max)}.`,
      quantity: (max, count) => `Masukkan nombor bulat dari 1 hingga ${count(max)}, atau biarkan kosong.`,
    },
    refused: {
      'source in use': 'Kempen lain sudah menggunakan nama pautan ini. Tukarnya di bawah Lagi terma.',
      'ends before it starts': 'Hari terakhir memberi adalah sebelum hari pertama.',
      'referral campaign open': 'Kempen jemputan lain sedang dibuka. Tamatkannya dahulu: hanya satu boleh berjalan pada satu masa.',
    },
    failed: { 'by link': 'Tidak dapat membuat QR. Cuba lagi.', 'by draw': 'Tidak dapat membuat cabutan. Cuba lagi.', 'by card': 'Tidak dapat membuat kad. Cuba lagi.', 'on join': 'Tidak dapat mula memberi. Cuba lagi.', referral: 'Tidak dapat memulakan jemputan. Cuba lagi.' },
  },
  voucherStation: {
    title: 'Dapatkan baucar anda',
    brandSub: 'Baucar',
    pressTitle: 'Dapatkan baucar anda',
    pressLead: 'Tekan butang, kemudian imbas QR dengan kamera telefon anda.',
    press: 'Tekan untuk baucar anda',
    drawing: 'Sedang mencabut',
    scanTitle: 'Imbas dengan telefon anda',
    scanLead: 'Buka kamera anda dan halakan ke kod. Ia boleh digunakan sekali, untuk seorang sahaja.',
    qrLabel: 'Kod QR baucar anda',
    left: (seconds) => `Kod ini hilang dalam ${String(seconds)} s`,
    done: 'Selesai',
    askStaffTitle: 'Sila tanya kakitangan',
    offline: 'Mesin ini tidak dapat menghubungi sistem buat masa ini. Kakitangan boleh membantu anda.',
    closed: 'Tiada baucar daripada mesin ini buat masa ini. Kakitangan boleh membantu anda.',
    tryAgain: 'Cuba lagi',
    unsetTitle: 'Peranti ini belum menjadi stesen baucar',
    unsetLead: 'Pengurus perlu log masuk di sini untuk menyediakannya bagi cabutan.',
    signIn: 'Log masuk untuk menyediakan',
    signInAsManager: 'Log masuk sebagai pengurus',
    setupTitle: 'Sediakan stesen baucar ini',
    setupLead: 'Pilih lokasi peranti ini dan cabutan yang diberinya. Anda akan dilog keluar selepas itu, dan peranti berjalan sendiri.',
    campaign: 'Cabutan',
    campaignPick: 'Pilih cabutan',
    campaignMissing: 'Pilih cabutan yang diberi oleh stesen ini.',
    noDraws: 'Tiada cabutan dibuka. Buat satu di halaman kempen dahulu.',
    name: 'Nama stesen',
    nameHint: 'Contohnya Pintu masuk.',
    nameMissing: 'Namakan stesen ini, contohnya Pintu masuk.',
    setupSubmit: 'Jadikan ini stesen baucar',
    setupFailed: 'Tidak dapat menyediakan peranti ini. Cuba lagi.',
  },
  stationLink: {
    lead: 'Buka pautan ini pada peranti di cawangan, dengan log masuk sebagai pengurus: ia menjadi stesen baucar bagi cabutan ini, dengan satu butang untuk pelanggan.',
  },
  defaultTerms: {
    open: 'Terma lalai',
    title: 'Terma lalai untuk baucar baharu',
    lead: 'Baucar baharu bermula dengan terma ini, dan anda boleh menukarnya untuk baucar itu. Menyimpan di sini hanya mengisi baucar baharu: ia tidak menukar baucar yang sudah dibuat.',
    leadBrands: 'Baucar baharu bermula dengan terma ini: terma jenama itu sendiri untuk baucar jenama tersebut, atau terma untuk semua jenama. Anda boleh menukarnya untuk setiap baucar. Menyimpan di sini hanya mengisi baucar baharu: ia tidak menukar baucar yang sudah dibuat.',
    scope: 'Lalai untuk',
    everyBrand: 'Semua jenama',
    label: 'Terma lalai',
    save: 'Simpan terma lalai',
    saving: 'Menyimpan',
    saved: 'Disimpan. Baucar baharu bermula dengan terma ini.',
    loadFailed: 'Tidak dapat memuatkan terma lalai.',
    failed: 'Tidak dapat menyimpan terma lalai. Cuba lagi.',
  },
  terms: {
    legend: 'Terma dan syarat',
    hintNew: 'Ahli membaca terma ini bersama baucar ini. Ia bermula sebagai terma lalai anda; tukar untuk baucar ini mengikut keperluan.',
    hintEdit: 'Ahli membaca terma ini bersama baucar ini. Baucar yang sudah diberikan mengekalkan terma asalnya.',
    line: (n) => `Terma ${String(n)}`,
    remove: (n) => `Buang terma ${String(n)}`,
    add: 'Tambah terma',
  },
  printRuns: {
    lead: 'Setiap kad mempunyai kod QR sendiri yang boleh digunakan sekali. Hantar fail kepada pencetak, atau cetak helaian di sini.',
    loading: 'Memuatkan',
    loadFailed: 'Tidak dapat memuatkan kad.',
    none: 'Belum ada kad dibuat.',
    serials: (first, last) => (first === last ? first : `${first} hingga ${last}`),
    made: (date) => `Dibuat ${date}`,
    cards: 'Kad',
    reward: 'Baucar',
    scanned: 'Diimbas',
    claimed: 'Dituntut',
    used: 'Digunakan',
    voided: 'Dibatalkan',
    download: 'Muat turun untuk pencetak',
    linksTo: (host) => `Kod QR kad membuka ${host}.`,
    notPublic: (host) => `Kod QR kad akan membuka ${host}, yang tidak dapat dicapai oleh telefon pelanggan. Muat turun untuk pencetak dari alamat awam.`,
    csvHeader: ['serial', 'code', 'link', 'voucher', 'label'],
    sheet: 'Cetak helaian QR',
    void: 'Batalkan kad yang hilang',
    voidLead: 'Kad yang dibatalkan akan ditolak apabila diimbas. Kad yang sudah dituntut mengekalkan baucarnya.',
    firstSerial: 'Dari kad',
    lastSerial: 'Hingga kad',
    voidSubmit: (cards, count) => (cards === null ? 'Batalkan' : `Batalkan ${count(cards)} kad`),
    voiding: 'Membatalkan',
    keep: 'Batal',
    invalidSerial: 'Masukkan nombor kad, contohnya 0021, yang kedua selepas yang pertama.',
    outside: 'Kad tersebut bukan semuanya dalam cetakan ini.',
    voidFailed: 'Tidak dapat membatalkan kad. Cuba lagi.',
    qrLabel: (serial) => `Kod QR kad ${serial}`,
    back: 'Kembali ke kempen',
  },
  campaignQr: {
    lead: 'Cetak atau paparkan pada skrin. Mengimbasnya membuka halaman pendaftaran dengan baucar ini.',
    label: (link) => `Kod QR untuk ${link}`,
    copy: 'Salin pautan',
    copied: 'Disalin',
    copyFailed: 'Tidak dapat menyalin. Pautan sudah dipilih: salin sendiri.',
    print: 'Cetak',
    saveSvg: 'Simpan sebagai SVG',
    savePng: 'Simpan sebagai PNG',
    back: 'Kembali ke kempen',
  },
  campaign: {
    crumb: 'Kempen',
    summary: (kind, vouchers, dates, given, used, count, top) => [`${kind} · `, { figure: count(vouchers) }, ' baucar · ', `${dates}. `, { figure: `${count(given)} diberikan` }, ', ', { figure: `${count(used)} digunakan` }, '.', ...(top === null ? [] : [` ${top} paling berkesan.`])],
    dates: (from, until) => (until === null ? `dari ${from}` : `${from} hingga ${until}`),
    exportCsv: 'Eksport CSV',
    addVoucher: 'Tambah baucar',
    given: 'Diberikan',
    givenNote: (heldBack, opened) => `${heldBack} ditahan · ${opened} dibuka`,
    used: 'Digunakan',
    usedNote: (rate) => `${rate} daripada yang diberikan`,
    valueGiven: 'Nilai diberikan',
    valueUsed: 'Nilai digunakan',
    valueNote: 'pada nilai muka',
    timeToUse: 'Masa sehingga digunakan',
    timeNote: (cancelled, expired, median) => `${median ? 'median, paling lama' : 'Belum ada yang digunakan'} · ${cancelled} dibatalkan · ${expired} tamat tempoh`,
    noneUsed: 'Tiada',
    minutes: (minutes) => `${minutes} min`,
    hours: (hours) => `${hours} j`,
    within: (time) => time,
    beyond: '> 30 hari',
    range: (from, to) => `${from} hingga ${to}`,
    totalOf: (vouchers, count) => `Jumlah · ${count(vouchers)} baucar`,
    days: (days) => `${days} hari`,
    split: 'Pecahan baucar',
    splitGiven: 'Diberikan',
    splitUsed: 'Digunakan',
    splitEmpty: 'Belum ada.',
    vouchersTitle: 'Setiap baucar',
    columns: { voucher: 'Baucar', cards: 'Kad', chance: 'Peluang', given: 'Diberikan', left: 'Baki', used: 'Digunakan', rate: 'Kadar guna', valueUsed: 'Nilai digunakan' },
    noLimit: 'Tiada had',
    chance: (weight, total, percent) => `${weight} daripada ${total}, ${percent}`,
    total: 'Jumlah',
    csvName: (name) => `Keputusan ${name}`,
    byMedium: 'Cara mereka datang',
    byMediumEmpty: 'Belum ada baucar diambil melalui pautan bertag.',
    qr: 'QR',
    qrTitle: (voucher) => `QR untuk ${voucher}`,
    qrGives: (vouchers) => (vouchers === 1 ? 'Mengimbasnya membuka halaman pendaftaran dan memberi baucar ini.' : 'Mengimbasnya membuka halaman pendaftaran dan memberi baucar ini sahaja, bukan baucar lain dalam kempen.'),
    campaignQr: 'QR kempen',
    campaignQrGives: (vouchers, randomOne) => (vouchers === 1 ? 'Mengimbasnya membuka halaman pendaftaran dan memberi baucar kempen.' : randomOne ? `Mengimbasnya membuka halaman pendaftaran dan memberi satu daripada ${String(vouchers)} baucar kempen secara rawak, mengikut pemberatnya. Setiap baucar juga mempunyai QR sendiri, dalam barisnya, yang memberi baucar itu.` : `Mengimbasnya membuka halaman pendaftaran dan memberi kesemua ${String(vouchers)} baucar kempen. Setiap baucar juga mempunyai QR sendiri, dalam barisnya.`),
  },
  mediums: { qr: 'Kod QR', link: 'Pautan', print: 'Kad bercetak', station: 'Stesen baucar' },
  catalogue: {
    title: 'Baucar',
    summary: (offers, given, used, count) => [{ figure: count(offers) }, ' baucar dalam katalog, ', { figure: count(given) }, ' diberikan, ', { figure: count(used) }, ' digunakan.'],
    newVoucher: 'Baucar baharu',
    loading: 'Memuatkan',
    loadFailed: 'Tidak dapat memuatkan baucar.',
    none: 'Belum ada baucar. Baucar baharu dimasukkan ke dalam kempen, atau mendapat QR sendiri.',
    columns: { voucher: 'Baucar', given: 'Diberikan', used: 'Digunakan', status: 'Status' },
    active: 'Dalam katalog',
    archived: 'Diarkibkan',
    revision: (revision) => `semakan ${String(revision)}`,
    name: 'Nama',
    nameHint: 'Ahli melihatnya pada baucar, contohnya Mangkuk 100 g percuma.',
    partOf: 'Sebahagian daripada kempen',
    campaign: 'Kempen',
    campaignPick: 'Pilih kempen',
    noCampaigns: 'Tiada kempen dibuka yang menerima baucar lain.',
    severalTake: 'Kempen QR acara, stesen baucar, kad bercetak dan penyertaan memberi beberapa baucar; kempen jemputan atau kumpulan sasaran memberi satu sahaja.',
    makeQr: 'Buat QR untuknya',
    makeQrHint: 'Kempen tersendiri: setiap orang yang mengimbas QR mendapat baucar ini sekali.',
    save: 'Simpan baucar',
    saving: 'Menyimpan',
    saved: (name) => `${name} kini dalam katalog.`,
    savedIn: (name, campaign) => `${name} kini dalam katalog, dan ${campaign} memberikannya sekarang.`,
    qrTitle: (name) => `QR untuk ${name}`,
    failed: 'Tidak dapat menyimpan baucar. Cuba lagi.',
    refused: (campaign, reason) => `Baucar ini dalam katalog, tetapi ${campaign} tidak menerimanya: ${reason}`,
    invalidName: 'Beri baucar ini nama.',
    invalidDays: 'Masukkan bilangan hari dalam nombor bulat, contohnya 30.',
    edit: (name) => `Sunting ${name}`,
    editTitle: 'Sunting baucar',
    editLead: (revision) => `Menyimpan akan membuat semakan ${String(revision + 1)} baucar ini. Baucar yang sudah diberikan mengekalkan semakan ${String(revision)} dan terma asalnya: tiada baucar ahli yang berubah.`,
    editTermsKept: 'Hari, waktu dan cawangannya kekal seperti sedia ada.',
    termsCount: (lines) => (lines === 0 ? 'Tiada terma dan syarat' : `${String(lines)} terma dan syarat`),
    editCampaigns: (names) => `Beri terma baharu dalam kempen yang memberikannya sekarang: ${names}`,
    editCampaignsHint: (revision) => `Jika tidak ditanda, kempen tersebut terus memberi semakan ${String(revision)} sehingga ditetapkan semula.`,
    picture: 'Gambar',
    pictureHint: 'PNG atau JPEG sehingga 2 MB, bentuk segi empat sama paling sesuai. Setiap baucar tawaran ini memaparkannya di halaman ahli, termasuk yang sudah diberi. Ia disimpan serta-merta, tanpa semakan baharu.',
    picturePick: 'Tambah gambar',
    pictureChange: 'Tukar gambar',
    pictureRemove: 'Buang',
    pictureUploading: 'Memuat naik',
    pictureTooBig: 'Gambar itu melebihi 2 MB. Pilih yang lebih kecil.',
    pictureWrongType: 'Pilih gambar PNG atau JPEG.',
    pictureFailed: 'Gambar tidak dapat disimpan. Cuba lagi.',
    editSave: 'Simpan semakan baharu',
    editSaved: (name, revision) => `${name} kini pada semakan ${String(revision)}.`,
    editSavedCampaigns: (count) => `${String(count)} kempen memberi terma baharu mulai sekarang.`,
  },
  voucher: {
    off: 'diskaun',
    offUpTo: (cap) => `diskaun, sehingga ${cap}`,
    free: 'Percuma',
    grams: (grams) => `${String(grams)} g`,
    everyDay: 'Setiap hari',
    anyTime: 'Bila-bila hari, bila-bila masa',
    day: { mon: 'Isn', tue: 'Sel', wed: 'Rab', thu: 'Kha', fri: 'Jum', sat: 'Sab', sun: 'Ahd' },
    from: (date) => `Dari ${date}`,
    until: (date) => `Hingga ${date}`,
    noEnd: 'Tiada tarikh tamat',
    goodFor: (days) => `Sah selama ${String(days)} hari`,
    foodOnly: 'Makanan sahaja, bukan minuman',
    minSpend: (amount) => `Belanja sekurang-kurangnya ${amount}`,
    minGrams: (grams) => `Pesan sekurang-kurangnya ${String(grams)} g`,
    conditions: 'Terma dan syarat',
    onePerBill: 'Satu baucar setiap bil',
    onlyAt: (outlets) => `Di ${outlets.join(', ')} sahaja`,
    brandOnly: (brands) => `Di cawangan ${brands.join(', ')} sahaja`,
    status: { usable: 'Sedia digunakan', redeemed: 'Digunakan', revoked: 'Ditarik balik', lapsed: 'Tamat tanpa digunakan' },
  },
};

export const t: CrmStrings = byLocale({ en, zh, ms });
